// One goods-in PO line: what D365 received, the certificates found for it, and
// the decisions.
//
// THE DECISION IS THE ONE QA STEP (Amit, 24 Oct 2026). Per batch the page shows
// the batch, the certificate files, anything OUT or FAIL in red, and the decision
// form for the newest live certificate naming it. Everything else (results to
// review, reasons, identity and signature, the optional attest and approve forms,
// provenance) is behind one collapsed Details per certificate. Attesting and
// approving names are optional: they only let a later certificate PASS on its own.
//
// THE DECISION IS MADE HERE AND NOWHERE ELSE (R6). Approving a certificate is
// what makes received goods usable, so it needs the RELEASE grant and a current
// RECEIPT_INSPECT competency, and the API refuses it from chat. Every live
// certificate can be decided — a line with two deliveries can carry two — and
// approval is per batch: a batch is approved only when an approval was made with
// it in front of the person. The screen applies the server's own rules before
// the click (decisionBlocker), so the person writes the reason instead of being
// refused for leaving it out.
//
// Each certificate was judged against the supplier's spec when it was recorded
// (Phase B, judgeCofa): its verdict, why, and each result's verdict are shown. A
// verdict recommends; the person decides, and approving a FAIL takes a written
// reason, as the server requires. Each value shows the words it was read from, and
// a value read by hand says so.
//
// Identity and signature (migration 016): the issuer, product and signature as printed, how each
// name matched signed data, and whether the server found each quote in the file. What the machine
// cannot prove a person stands behind here — attesting identity or signature, or approving a printed
// issuer or product name as signed data — and never the person who recorded the certificate: the
// buttons are hidden for them, and the server refuses them whatever the screen shows.
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircleCheck, CircleMinus, CircleX, TriangleAlert } from 'lucide-react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, buttonVariants } from '@/components/ui/button';
import { CertificateFile } from './CertificateFile';
import { Breadcrumb } from '@/components/Breadcrumb';
import { usePageTitle } from '../../lib/pageTitle';
import { Fact } from '@/components/PageFacts';
import { DataTable } from '@/components/DataTable';
import { VerdictBadge } from '../../components/VerdictBadge';
import { api, errorMessage } from '../../lib/api';
import { foundByLabel } from '../../lib/vocabulary';
import { currentUpn } from '../../config/auth';
import {
  fieldClass,
  grantHint,
  labelClass,
  officeFocus,
  serverMessage,
  useMyGrants,
  useResource,
  subjectKey,
  type MyGrants,
  type SubjectType,
} from '../../shells/OfficeShell';
import { NcrPanel, useNcrConfig, type NcrConfig } from './RaiseNcr';
import { PutOnHold, useHoldConfig, type HoldConfig } from './BatchHolds';
import {
  ARRIVES_LABEL,
  AUTOMATIC_APPROVAL,
  AUTOMATIC_APPROVAL_WHY,
  ATTESTATION_LABEL,
  CERTIFICATE_TYPE_LABEL,
  ISSUER_KIND_LABEL,
  NAME_MATCH_LABEL,
  NAME_MATCH_TONE,
  SIGNATURE_LABEL,
  recordersOf,
  visibleText,
  type AttestationKind,
  type IssuerKind,
  DECISION_LABEL,
  FLAG_LABEL,
  FLAG_TONE,
  IIR_LABEL,
  IIR_TONE,
  correctionPayload,
  goodsAfterVehicle,
  handoffOrder,
  stageOf,
  STAGE_LABEL,
  iirMatchText,
  failureSentence,
  notAskedText,
  lineagePhotos,
  photoCaption,
  READ_MODE_LABEL,
  SOURCE_LABEL,
  STATE_LABEL,
  STATE_TONE,
  TONE_CLASS,
  TONE_TEXT,
  chipClass,
  dayLabel,
  decisionBlocker,
  receivedWithUnit,
  shortDay,
  stamp,
  type Certificate,
  type CofaDecision,
  type Coverage,
  type IirCheck,
  type Inspection,
  type InspectionPhoto,
  type LotMatch,
  type ReceiptDetail,
  type SpecLine,
  recordedVia,
  type Tone,
} from './goodsInModel';
import {
  attestedKinds,
  decisionGroups,
  nextLines,
  noDecisionText,
  judgedAgainstSpec,
  missingAcross,
  outOfSpec,
  batchGroupTitle,
  snapshotAge,
  type DecisionGroup,
} from './receiptSummary';

const MATCH_LABEL: Record<LotMatch, string> = { EXACT: 'Exact', NEAR: 'Near', NONE: 'Not named' };
const MATCH_TONE: Record<LotMatch, keyof typeof TONE_CLASS> = { EXACT: 'ok', NEAR: 'warn', NONE: 'bad' };

const cardClass =
  'rounded-(--radius) border border-(--border) bg-(--card) p-(--s4) shadow-card';

type BatchRow = ReceiptDetail['batches'][number] & { coverage: Coverage['batches'][number] | null };
const batchCol = createColumnHelper<BatchRow>();
/** A batch received before the cutover is the paper system's: neither approved
 *  here nor a failure to approve, so it is said plainly and never painted red. */
const approvedText = (b: BatchRow) =>
  b.onPaper ? 'On paper' : b.approved === null ? 'Unknown' : b.approved ? 'Yes' : 'No';
const approvedTone = (b: BatchRow) =>
  (b.onPaper || b.approved === null ? 'quiet' : b.approved ? 'ok' : 'bad') as keyof typeof TONE_CLASS;

const batchColumns = [
  batchCol.accessor('batchId', { header: 'Batch', meta: { rowHeader: true, mono: true, nowrap: true } }),
  batchCol.accessor((b) => b.packingSlipId ?? '', {
    id: 'slip',
    header: 'Packing slip',
    cell: (c) => c.getValue() || '—',
    meta: { mono: true, nowrap: true },
  }),
  batchCol.accessor((b) => b.qty ?? -1, {
    id: 'qty',
    header: 'Qty',
    cell: (c) => c.row.original.qty ?? '—',
    meta: { align: 'right', mono: true, nowrap: true },
  }),
  batchCol.accessor((b) => b.receivedDate ?? '', {
    id: 'received',
    header: 'Received',
    cell: (c) => dayLabel(c.row.original.receivedDate),
    meta: { nowrap: true },
  }),
  // Always with the year: made "29 Apr" and best before "28 Apr" read as expired.
  batchCol.accessor((b) => b.manufacturedDate ?? '', {
    id: 'made',
    header: 'Made',
    cell: (c) => dayLabel(c.row.original.manufacturedDate, true),
    meta: { nowrap: true },
  }),
  batchCol.accessor((b) => b.bestBeforeDate ?? '', {
    id: 'bestBefore',
    header: 'Best before',
    cell: (c) => dayLabel(c.row.original.bestBeforeDate, true),
    meta: { nowrap: true },
  }),
  batchCol.accessor((b) => b.disposition ?? '', {
    id: 'disposition',
    header: 'Disposition',
    cell: (c) => c.getValue() || '—',
  }),
  batchCol.accessor(approvedText, {
    id: 'approved',
    header: 'Approved',
    cell: (c) => <span className={`${chipClass} ${TONE_CLASS[approvedTone(c.row.original)]}`}>{c.getValue()}</span>,
    meta: { nowrap: true },
  }),
  batchCol.accessor((b) => (b.coverage ? MATCH_LABEL[b.coverage.match] : 'no certificate yet'), {
    id: 'coverage',
    header: 'On the newest certificate',
    enableSorting: false,
    cell: (c) => {
      const cov = c.row.original.coverage;
      // On paper by the register's own answer for the line — the one that drives
      // the flags — so this column never says "on paper" beside a red row.
      if (c.row.original.onPaper) return <span className="text-(--muted)">on paper</span>;
      return cov ? (
        <>
          <span className={`${chipClass} whitespace-nowrap ${TONE_CLASS[MATCH_TONE[cov.match]]}`}>
            {MATCH_LABEL[cov.match]}
            {cov.lot && cov.match !== 'NONE' ? ` · ${cov.lot}` : ''}
          </span>
          {cov.note ? (
            <span className="block max-w-[40ch] text-(length:--fs-xs) text-(--muted)">{cov.note}</span>
          ) : null}
        </>
      ) : (
        <span className="text-(--muted)">no certificate yet</span>
      );
    },
  }),
];

type ResultRow = Certificate['results'][number];
const resultCol = createColumnHelper<ResultRow>();
/** The server's sentence for a result it could not pair to a spec check: shown as a chip, not a row of prose. */
const NOT_IN_SPEC = 'Not in the spec.';
const resultColumns = [
  resultCol.accessor('parameter', {
    header: 'Parameter',
    meta: { rowHeader: true },
    cell: (c) => (
      <>
        {c.getValue()}
        {c.row.original.verdictReason === NOT_IN_SPEC ? (
          <span className={`${chipClass} ml-(--s2) ${TONE_CLASS.quiet}`}>not in spec</span>
        ) : null}
      </>
    ),
  }),
  resultCol.accessor((x) => `${x.result}${x.unit && !x.result.includes(x.unit) ? ` ${x.unit}` : ''}`, {
    id: 'result',
    header: 'Result',
    // The words it was read from, on hover: the file itself is one click away under "Where this came from".
    cell: (c) => {
      const x = c.row.original;
      return <span title={x.quote ? `Read from "${x.quote}"${x.page ? ` · p${x.page}` : ''}` : 'No quote (read by hand)'}>{c.getValue()}</span>;
    },
    meta: { mono: true, nowrap: true },
  }),
  resultCol.accessor((x) => x.printedLimit ?? '', {
    id: 'limit',
    header: "Supplier's limit",
    cell: (c) => c.getValue() || '—',
  }),
  resultCol.accessor((x) => x.verdict ?? '', {
    id: 'verdict',
    header: 'Verdict',
    cell: (c) => {
      const why = c.row.original.verdictReason;
      return (
        <span className="flex flex-wrap items-center gap-x-(--s2)">
          <VerdictBadge verdict={c.row.original.verdict ?? null} />
          {why && why !== NOT_IN_SPEC ? <span className="text-(length:--fs-xs) text-(--muted)">{why}</span> : null}
        </span>
      );
    },
  }),
];

/** What a certificate's verdict says, and no more (rule 11). */
const VERDICT_TEXT = {
  PASS: 'Every check in, against an approved supplier spec.',
  FAIL: 'A result is outside the approved supplier spec.',
  NEEDS_REVIEW: 'A person has to read it, for the reasons below.',
  NOT_ASSESSED: 'There was no supplier spec to judge it against.',
} as const;

/** The verdict's sentence, in the past once a person has decided (UI/UX audit, 31 Oct 2026, UX-49: an
 *  approved certificate still said "A person has to read it"). Only the words change: the verdict chip
 *  is the server's and stays as recorded (rule 6). */
export function verdictText(verdict: keyof typeof VERDICT_TEXT, decided: boolean): string {
  return decided && verdict === 'NEEDS_REVIEW' ? 'A person had to read it, for the reasons below.' : VERDICT_TEXT[verdict];
}

/** The server's sentences carry the rule they rest on ("(rule 25)") and the grant that lets a person
 *  act ("a SPEC_APPROVE holder"); QA reads neither (Amit, 27 Oct 2026). Said in their words here. */
export function plainWords(s: string): string {
  return s
    .replace(/\s*\(rule \d+\)/g, '')
    .replace(/a SPEC_APPROVE holder/g, 'someone who approves specs')
    .replace(/a RELEASE holder/g, 'someone who releases goods')
    .replace(/a CAPTURE holder/g, 'someone who records checks');
}

const h3Class = 'm-0 text-(length:--fs-sm) font-semibold';
const mutedSm = 'm-0 text-(length:--fs-sm) text-(--muted)';
const summaryClass = `cursor-pointer text-(length:--fs-sm) font-semibold ${officeFocus}`;

/** One fact in the header row: a small label over its value. */
export function GoodsInReceipt() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const id = Number(receiptId);
  const valid = Number.isInteger(id) && id > 0;
  const detail = useResource<ReceiptDetail>(valid ? `/receipts/${id}` : null);
  const grants = useMyGrants();
  const ncrConfig = useNcrConfig();
  const holdConfig = useHoldConfig();
  // The tab and the compact bar name the PO, not only the register (UI/UX audit, 31 Oct 2026, UX-20):
  // "PO024474 · Goods in · Quality" - a PO page used to read "Goods in · Quality", like the register.
  usePageTitle(detail.data ? `${detail.data.po}${detail.data.line !== 1 ? ` / ${detail.data.line}` : ''}` : null);

  if (!valid) {
    return (
      <p role="alert" className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
        That address does not name a goods-in line. Open one from the register.
      </p>
    );
  }
  if (detail.error) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
        <p className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">{detail.error}</p>
        <Button type="button" variant="outline" size="sm" onClick={detail.reload}>
          Try again
        </Button>
      </div>
    );
  }
  const d = detail.data;
  if (!d) return <p className="m-0 text-(length:--fs-md) text-(--muted)">Loading…</p>;

  const current = d.certificates.find((c) => c.cofaId === d.certificate?.cofaId) ?? null;
  const superseded = d.certificates.filter((c) => c.supersededBy !== null);
  // The job's record replaced by the job (UX-18): folded into one line below.
  const rerecorded = superseded.filter(
    (c) => c.door === 'SYSTEM' && d.certificates.find((n) => n.cofaId === c.supersededBy)?.door === 'SYSTEM',
  );

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <Breadcrumb items={[{ label: 'Goods in', to: '/office/goods-in' }, { label: `${d.po}${d.line !== 1 ? ` / ${d.line}` : ''}` }]} />
        <h1 className="m-0 mt-(--s1) font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">
          {d.po}
          {d.line !== 1 ? ` / ${d.line}` : ''} · {d.itemName ?? d.item}
        </h1>
        <dl className="m-0 mt-(--s3) flex flex-wrap gap-x-(--s5) gap-y-(--s2)">
          <Fact label="Supplier">{d.vendor.name ?? d.vendor.account ?? 'unknown'}</Fact>
          <Fact label="Item">
            <span className="font-mono">{d.item}</span>
          </Fact>
          <Fact label="Due">{dayLabel(d.expectedDate)}</Fact>
          <Fact label="Ordered">
            <span className="font-mono tabular-nums">{d.orderedQty ?? '—'}</span> {d.unit ?? ''}
          </Fact>
          {/* "2100 kg" once the stock unit is known (migration 028); until then, the old label. */}
          <Fact label={receivedWithUnit(d) ? 'Received' : 'Received (stock unit)'}>
            <span className="font-mono tabular-nums">{receivedWithUnit(d) ?? d.receivedQty ?? '—'}</span>
          </Fact>
          <Fact label="D365 says">{d.lineStatus ?? '—'}</Fact>
          <Fact label="Snapshot">
            <span className={snapshotAge(d.syncedAt) === 'fresh' ? undefined : 'font-semibold text-(--warn)'}>
              {d.syncedAt ? stamp(d.syncedAt) : 'Not known'}
            </span>
          </Fact>
        </dl>
        {/* Over a day old (the auto-approve sweep's snapshot_stale threshold) it is said, with how to
            refresh it: the register's Refresh reads D365 for a window of days (UI/UX audit, 31 Oct 2026,
            UX-28). A per-line refresh is a follow-up. */}
        {snapshotAge(d.syncedAt) !== 'fresh' ? (
          <p className="m-0 mt-(--s2) max-w-[80ch] text-(length:--fs-sm) text-(--warn)">
            {d.syncedAt
              ? `This line was last read from D365 on ${stamp(d.syncedAt)}, over a day ago, so what it says about the goods may be out of date. `
              : 'When this line was last read from D365 is not known, so what it says about the goods may be out of date. '}
            <Link to="/office/goods-in" className={`font-semibold underline underline-offset-2 ${officeFocus}`}>
              Refresh from D365 on the register
            </Link>
            {/* The register syncs lines by the day goods were RECEIVED, not the PO's due date (issue
                #156, 31 Oct 2026), so the window to name is the newest receipt; the due date only
                when nothing has been received yet. */}
            {(() => {
              const received = d.batches.map((b) => b.receivedDate).filter((x): x is string => !!x).sort().at(-1);
              const day = received ?? d.expectedDate;
              return day ? ` with dates that include ${dayLabel(day, true)}${received ? ', the day it was received' : ''}.` : '.';
            })()}
          </p>
        ) : null}
      </header>

      <section aria-label="Status" className="flex flex-wrap items-center gap-(--s2)">
        <span className={`${chipClass} ${TONE_CLASS[STATE_TONE[d.state]]}`}>{STATE_LABEL[d.state]}</span>
        {d.flags.map((f) => (
          <span key={f} className={`${chipClass} ${TONE_CLASS[FLAG_TONE[f]]}`}>
            {FLAG_LABEL[f]}
          </span>
        ))}
        {d.batchReadError ? (
          <p role="alert" className="m-0 w-full text-(length:--fs-sm) font-semibold text-(--bad)">
            The batches for this line could not be read from D365 on the last refresh: {d.batchReadError}
          </p>
        ) : null}
      </section>

      {d.certificates.length === 0 && d.state === 'ON_PAPER' ? (
        <section className={cardClass}>
          <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Handled on paper</h2>
          <p className="m-0 mt-(--s2) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
            These goods were received before the register took over
            {d.cutover ? ` on ${dayLabel(d.cutover, true)}` : ''}. Their certificate was dealt with on paper, so
            nothing is asked for here and nothing is chased.
          </p>
        </section>
      ) : d.certificates.length === 0 ? (
        <section className={cardClass}>
          <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">No certificate yet</h2>
          <p className="m-0 mt-(--s2) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
            This supplier sends certificates {ARRIVES_LABEL[d.supplierRule.cofaArrives]}.{' '}
            {d.requestDueOn ? `A request is due on ${shortDay(d.requestDueOn)}. ` : ''}
            Certificates emailed to qa@ are found and recorded automatically by the intake job.
          </p>
          {/* An email the job could not match waits in the intake for a person (UI/UX audit, 31 Oct 2026,
              UX-49): until now this card had no way there. */}
          <Link to="/office/goods-in?tab=intake" className={`mt-(--s2) inline-block text-(length:--fs-sm) font-semibold underline underline-offset-2 ${officeFocus}`}>
            Look in the intake for unmatched emails
          </Link>
          <RequestCoa receiptId={d.receiptId} onSent={detail.reload} />
        </section>
      ) : (
        <Decide d={d} grants={grants} onRead={detail.reload} ncrConfig={ncrConfig} holdConfig={holdConfig} />
      )}


      <details className={cardClass}>
        <summary className={summaryClass}>
          <SectionTitle title={`Batches in D365 (${d.batchReadError ? 'not known' : d.batches.length})`} chip={batchesChip(d)} />
        </summary>
        {d.batches.length === 0 ? (
          <p className="m-0 mt-(--s2) text-(length:--fs-sm) text-(--muted)">
            {d.batchReadError
              ? 'Unknown: D365 could not be read.'
              : d.received
                ? 'D365 records the goods as received but holds no batch for them (the item may not be batch-tracked).'
                : 'Nothing receipted in D365 yet. The batch only exists once the goods are received.'}
          </p>
        ) : (
          <div className="mt-(--s3)">
            <DataTable
              data={d.batches.map((b) => ({
                ...b,
                coverage:
                  current?.coverage?.batches.find(
                    (x) => x.batchId === b.batchId && (x.packingSlipId ?? '') === (b.packingSlipId ?? ''),
                  ) ?? null,
              }))}
              columns={batchColumns}
              caption="Batches D365 received this line into"
              getRowId={(b) => `${b.batchId}-${b.packingSlipId ?? ''}`}
              rowAccent={(b) => (b.approved === false && !b.onPaper ? 'bad' : null)}
              search={d.batches.length > 10 ? 'Search batches' : undefined}
              maxHeight="28rem"
            />
          </div>
        )}
      </details>

      <InspectionBlock inspections={d.inspections} receiptId={d.receiptId} received={d.received === true} grants={grants} onCorrected={detail.reload} />

      {d.certificates.some((c) => c.supersededBy === null) ? (
        <details className={cardClass}>
          <summary className={summaryClass}>
            {/* What the person does here, not what the pipeline learns (UI/UX audit, 31 Oct 2026, UX-18). */}
            <SectionTitle title="Supplier names and signatures to approve" chip={teachChip(d.certificates)} />
          </summary>
          <p className="m-0 mt-(--s2) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
            Not needed to decide a batch. Approving an issuer or a product name here, or attesting what a
            certificate shows, lets future certificates from this supplier prove themselves without a person.
          </p>
          {d.certificates
            .filter((c) => c.supersededBy === null)
            .map((c) => (
              <div key={c.cofaId} className="mt-(--s3)">
                <h3 className={h3Class}>Certificate #{c.cofaId}</h3>
                <IdentityLines
                  c={c}
                  live
                  recorder={recordersOf(d.certificates, c.cofaId).has(currentUpn().toLowerCase())}
                  attested={attestedKinds(d.certificates, c.cofaId)}
                  grants={grants}
                  onDone={detail.reload}
                />
              </div>
            ))}
        </details>
      ) : null}

      <details className={cardClass}>
        <summary className={summaryClass}>
          <SectionTitle
            title="Supplier, approved issuers and chases"
            chip={
              d.supplierRule.isDefault || !d.supplierRule.senderDomains
                ? { text: 'Intake cannot match its emails', tone: 'warn' }
                : { text: `${d.chaseHistory.length} chase${d.chaseHistory.length === 1 ? '' : 's'}`, tone: 'quiet' }
            }
          />
        </summary>
        <p className="m-0 mt-(--s2) text-(length:--fs-sm)">
          {d.supplierRule.isDefault ? 'No settings for this supplier yet; the default applies. ' : ''}
          Sends certificates {ARRIVES_LABEL[d.supplierRule.cofaArrives]} · quotes: {d.supplierRule.matchKey}
          {d.supplierRule.requestLeadDays !== null ? ` · ask ${d.supplierRule.requestLeadDays} days before delivery` : ''}
          {d.supplierRule.senderDomains ? ` · sends from ${d.supplierRule.senderDomains}` : ' · no sender domain, so the intake job cannot match its emails'}
          {d.supplierRule.contactEmail ? ` · requests go to ${d.supplierRule.contactEmail}` : ''}
          {d.supplierRule.lotFormat === 'DATE_CODE_PALLET' ? ' · its date code covers its pallets' : d.supplierRule.lotFormat === 'REFERENCE_SUFFIX' ? ' · its certificate reference is the batch' : ''}
          {d.supplierRule.autoApprove === false ? ' · auto-approve off' : ''}
        </p>
        <Link to="/office/goods-in?tab=suppliers" className={`${buttonVariants({ variant: 'outline', size: 'sm' })} mt-(--s2)`}>
          Edit supplier settings
        </Link>
        {d.supplierRule.notes ? (
          <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">{d.supplierRule.notes}</p>
        ) : null}
        <h3 className="m-0 mt-(--s3) text-(length:--fs-md) font-semibold">Who may issue its certificates</h3>
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
          The supplier on the order, and the manufacturers and laboratories someone who approves specs approved from a certificate.
        </p>
        {d.approvedIssuers === undefined ? (
          // Rule 15: a list that did not arrive is not an empty list (review round 1, #10).
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
            The approved issuers were not in the server's answer, so none is claimed either way. Reload the page.
          </p>
        ) : d.approvedIssuers.length ? (
          <ul className="m-0 mt-(--s1) list-none p-0 text-(length:--fs-sm)">
            {d.approvedIssuers.map((x) => (
              <li key={x.issuerId}>
                <strong>{x.issuerName}</strong> · {ISSUER_KIND_LABEL[x.kind]} · {x.d365ItemId ? `item ${x.d365ItemId} only` : 'any item'} ·
                approved by {x.approvedByUpn} on {stamp(x.approvedAt)} from certificate #{x.evidenceCofaId}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">None approved besides the supplier itself.</p>
        )}
        <h3 className="m-0 mt-(--s3) text-(length:--fs-md) font-semibold">Material names</h3>
        {d.material ? (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm)">
            Material #{d.material.materialId}: <strong>{d.material.name}</strong>
            {d.material.aliases.length
              ? ` · also approved: ${d.material.aliases.map((a) => `${a.alias} (by ${a.approvedByUpn}, from #${a.evidenceCofaId})`).join('; ')}`
              : ' · no other name approved'}
          </p>
        ) : (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">No material is set up for this item, so no product name can match.</p>
        )}
        <h3 className="m-0 mt-(--s3) text-(length:--fs-md) font-semibold">Chases</h3>
        {d.chaseHistory.length === 0 ? (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">None logged.</p>
        ) : (
          <ul className="m-0 mt-(--s1) list-none p-0 text-(length:--fs-sm)">
            {d.chaseHistory.map((c, i) => (
              <li key={i}>
                {stamp(c.loggedAt)} · {c.kind.toLowerCase()} {c.sentTo ? `to ${c.sentTo}` : ''} · by {c.loggedByUpn}
                {c.note ? ` · ${c.note}` : ''}
              </li>
            ))}
          </ul>
        )}
      </details>

      {superseded.length > 0 ? (
        <details className={cardClass}>
          <summary className={summaryClass}>
            <SectionTitle title={`Earlier versions (${superseded.length})`} chip={{ text: 'History', tone: 'quiet' }} />
          </summary>
          {/* The intake job re-recording its own certificate (rules changed, a spec approved) is one
              line, not a full card each (UI/UX audit, 31 Oct 2026, UX-18): most of this list was
              that, and it buried the corrections a person made. */}
          {rerecorded.length > 0 ? (
            <p className="m-0 mt-(--s3) text-(length:--fs-sm) text-(--muted)">
              Re-recorded automatically by the CofA intake job when its judging rules or the spec changed:{' '}
              {rerecorded.map((c) => `#${c.cofaId} (${stamp(c.recordedAt)})`).join(', ')}.
            </p>
          ) : null}
          {superseded.filter((c) => !rerecorded.includes(c)).map((c) => (
            <div key={c.cofaId} className="mt-(--s3) border-t border-(--border) pt-(--s3)">
              <CertificateHead c={c} />
              <CertificateDetails
                c={c}
                decideHere={false}
                checklistAbove={false}
                batchReadError={d.batchReadError}
                grants={grants}
                onDecided={detail.reload}
              />
            </div>
          ))}
        </details>
      ) : null}
    </div>
  );
}

/** A collapsed section's title with its answer beside it, so the reader knows from the closed card
 *  whether to open it (UI audit, 29 Oct 2026). */
function SectionTitle({ title, chip }: { title: string; chip: { text: string; tone: Tone } | null }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-(--s2) align-middle">
      <span>{title}</span>
      {chip ? <span className={`${chipClass} ${TONE_CLASS[chip.tone]}`}>{chip.text}</span> : null}
    </span>
  );
}

/** The batches' answer: approved or not, never "0" for a read that failed (rule 15). */
export function batchesChip(d: Pick<ReceiptDetail, 'batches' | 'batchReadError'>): { text: string; tone: Tone } | null {
  if (d.batchReadError) return { text: 'Not known', tone: 'warn' };
  const ours = d.batches.filter((b) => !b.onPaper);
  if (ours.length === 0) return d.batches.length ? { text: 'On paper', tone: 'quiet' } : null;
  const open = ours.filter((b) => b.approved !== true).length;
  return open === 0 ? { text: 'All approved', tone: 'ok' } : { text: `${open} not approved`, tone: 'warn' };
}

/** What is left to teach: live certificates whose identity or signature is not proven. */
export function teachChip(certs: ReceiptDetail['certificates']): { text: string; tone: Tone } | null {
  const live = certs.filter((c) => c.supersededBy === null);
  const known = live.filter((c) => c.proven);
  if (known.length === 0) return null;
  const open = known.filter((c) => !c.proven!.identity || !c.proven!.signed).length;
  return open === 0 ? { text: 'All proven', tone: 'ok' } : { text: `${open} not proven`, tone: 'quiet' };
}

/** The decision, per batch (Amit, 24 Oct 2026: the decision is the one QA step). Next says, per batch,
 *  "decide" or who decided what; each card then shows the batch, the certificate files, anything out of
 *  spec in red, and the decision form for the newest live certificate naming it. Everything else is
 *  behind one collapsed Details per certificate. Rule 15: when D365's batches could not be read,
 *  nothing is said per batch - one "not known" line and a way to read D365 again. */
function Decide({ d, grants, onRead, ncrConfig, holdConfig }: { d: ReceiptDetail; grants: MyGrants; onRead: () => void; ncrConfig: NcrConfig | null; holdConfig: HoldConfig | null }) {
  const groups = decisionGroups(d.certificates, d.batches, d.batchReadError, d.noDecisionNeeded);
  const targets = new Set(groups.flatMap((g) => (g.target ? [g.target.cofaId] : [])));
  return (
    <>
      <section className={cardClass} aria-label="Next steps">
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Next</h2>
        {d.batchReadError ? (
          <>
            <p className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
              Not known: the batches could not be read from D365, so no batch is said to be covered or not.
            </p>
            <ReadD365Again expectedDate={d.expectedDate} onRead={onRead} />
          </>
        ) : (
          <ul className="m-0 mt-(--s2) flex list-none flex-col gap-(--s1) p-0">
            {/* Identical lines fold into one (UI/UX audit, 31 Oct 2026, UX-12). */}
            {nextLines(groups).map((n) => (
              <li key={n.key} className={`text-(length:--fs-sm) font-semibold ${TONE_TEXT[n.tone]}`}>
                {n.text}
              </li>
            ))}
          </ul>
        )}
      </section>
      {groups.map((g) => (
        <DecisionCard key={g.key} g={g} d={d} grants={grants} onDecided={onRead} targets={targets} ncrConfig={ncrConfig} holdConfig={holdConfig} />
      ))}
    </>
  );
}

function DecisionCard({
  g,
  d,
  grants,
  onDecided,
  targets,
  ncrConfig,
  holdConfig,
}: {
  g: DecisionGroup<ReceiptDetail['batches'][number]>;
  d: ReceiptDetail;
  grants: MyGrants;
  onDecided: () => void;
  targets: ReadonlySet<number>;
  ncrConfig: NcrConfig | null;
  holdConfig: HoldConfig | null;
}) {
  const out = outOfSpec(g.certificates);
  // The ticks (Amit, 27 Oct 2026): each flagged result confirmed against the certificate by the person
  // deciding. Client state only; what they confirmed goes into the decision's note, which IS the record.
  const [checked, setChecked] = useState<ReadonlySet<number>>(() => new Set());
  const toggle = (sequence: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(sequence)) next.delete(sequence);
      else next.add(sequence);
      return next;
    });
  const suggestedNote = g.target ? checkedNote(g.target, checked) : '';
  // An NCR is raised on a batch whose certificate is OUT of spec, FAILED or was REJECTED (rule 29).
  const rejected = g.certificates.find((c) => c.decisions[c.decisions.length - 1]?.decision === 'REJECTED');
  const ncrCofa = out.failed[0] ?? out.results[0]?.cofaId ?? rejected?.cofaId ?? null;
  const badBatch = {
    lines: [{ item: d.item, label: d.itemName ?? d.item, batches: g.batches.map((b) => b.batchId) }],
    description: [
      ...out.results.map((r) => `${r.parameter} ${r.result}${r.limit ? ` (limit ${r.limit})` : ''} out of spec on certificate #${r.cofaId}`),
      ...out.failed.map((id) => `Certificate #${id} failed against the supplier spec`),
      ...(rejected ? [`Certificate #${rejected.cofaId} rejected`] : []),
    ].join('; '),
  };
  const title = g.batches.length
    ? batchGroupTitle(g.batches)
    : g.matchesNoBatch
      ? 'Certificates matching no batch received'
      : 'This line';
  return (
    <section className={cardClass} aria-label={title}>
      <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">{title}</h2>
      {/* Up to three batches are listed with their quantity and day; more are not (UI/UX audit, 31 Oct
          2026, UX-25: forty lines before the decision). Each is in Batches in D365 below. */}
      {g.batches.length > 3 ? (
        <p className={`${mutedSm} mt-(--s1)`}>Each batch's quantity and received day are under Batches in D365 below.</p>
      ) : g.batches.length ? (
        <ul className="m-0 mt-(--s1) list-none p-0 text-(length:--fs-sm)">
          {g.batches.map((b) => (
            <li key={`${b.batchId}-${b.packingSlipId ?? ''}`}>
              <span className="font-mono">{b.batchId}</span> · qty <span className="font-mono tabular-nums">{b.qty ?? '—'}</span> · received{' '}
              {dayLabel(b.receivedDate)}
            </li>
          ))}
        </ul>
      ) : !g.matchesNoBatch ? (
        <p className={`${mutedSm} mt-(--s1)`}>
          {d.batchReadError
            ? 'Batches not known: D365 could not be read.'
            : d.received
              ? 'Received; D365 holds no batch for it.'
              : 'Not receipted in D365 yet.'}
        </p>
      ) : null}

      {g.certificates.length ? (
        // The kept file opens here (CertificateFile, 27 Oct 2026), on the line a reader sees first -
        // not only inside the collapsed details. No file kept: it says to open the email in QA@.
        <ul className="m-0 mt-(--s2) flex list-none flex-col gap-(--s1) p-0 text-(length:--fs-sm)">
          {g.certificates.map((c) => (
            <li key={c.cofaId} className="flex flex-wrap items-center gap-(--s2)">
              <span>
                #{c.cofaId} <span className="font-mono">{c.source.attachmentName ?? SOURCE_LABEL[c.sourceKind]}</span>
              </span>
              {c.sourceKind === 'EMAIL' || c.source.sha256 ? <CertificateFile c={c} /> : null}
              {/* The spec this certificate was judged against (rule 6), not the one in force now (QA team, 29 Oct 2026). */}
              {c.specVersionId ? (
                <ViewSpecification versionId={c.specVersionId} from={`/office/goods-in/${d.receiptId}`} />
              ) : (
                <span className={mutedSm}>No specification: not judged</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {g.target ? (
        <>
          <ChecksTable c={g.target} d={d} checked={checked} onToggle={toggle} onCommented={onDecided} />
        </>
      ) : null}

      {out.results.length || out.failed.length ? (
        <ul aria-label="Out of spec" className="m-0 mt-(--s2) list-none p-0 text-(length:--fs-sm) font-semibold text-(--bad)">
          {out.results.map((r, i) => (
            <li key={`${r.cofaId}-${i}`}>
              {r.parameter} {r.result}
              {r.limit ? ` (limit ${r.limit})` : ''} out of spec on #{r.cofaId}
            </li>
          ))}
          {out.failed.map((id) => (
            <li key={id}>#{id} failed against the supplier spec</li>
          ))}
        </ul>
      ) : null}

      {ncrCofa !== null && g.batches.length ? (
        <>
          <NcrPanel config={ncrConfig} source={{ kind: 'COFA', id: ncrCofa }} grants={grants} prefill={{ vendor: d.vendor.account, ...badBatch }} />
          {/* QC Hold (rule 30): the same OUT / FAIL / REJECTED batch, the same words. */}
          <PutOnHold config={holdConfig} source={{ kind: 'COFA', id: ncrCofa }} grants={grants} prefill={badBatch} />
        </>
      ) : null}

      {g.target ? (
        <DecisionPanel
          certificate={g.target}
          batchReadError={d.batchReadError}
          grants={grants}
          onDecided={onDecided}
          suggestedNote={suggestedNote}
          configs={{ ncr: ncrConfig, hold: holdConfig }}
        />
      ) : (
        <p className={`${mutedSm} mt-(--s2)`}>No certificate names {g.batches.length === 1 ? 'this batch' : 'these batches'} yet.</p>
      )}

      {g.certificates.map((c) => (
        // The record: where it came from, what it says in full, who decided what. Folded, because
        // none of it changes the decision above (Amit, 27 Oct 2026: the details were "really noisy").
        <details key={c.cofaId} className="mt-(--s3)">
          <summary className={summaryClass}>Record of certificate #{c.cofaId}</summary>
          <div className="mt-(--s2)">
            {g.noDecision.has(c.cofaId) ? (
              <p className={`${mutedSm} mb-(--s2)`}>{noDecisionText(g.noDecision.get(c.cofaId)!)}</p>
            ) : null}
            <CertificateHead c={c} />
            {c === g.target ? <MissingAcross group={g.certificates} /> : null}
            <CertificateDetails
              c={c}
              decideHere={!targets.has(c.cofaId)}
              checklistAbove={c === g.target}
              batchReadError={d.batchReadError}
              grants={grants}
              onDecided={onDecided}
              configs={{ ncr: ncrConfig, hold: holdConfig }}
            />
          </div>
        </details>
      ))}
    </section>
  );
}

/* ── The QA checklist on the batch card (Amit, 27 Oct 2026) ─────────
 * Written the way the person releasing a batch asks it: right goods? results against our spec?
 * then decide. Everything the intake job or the server says about HOW it knows lives in the
 * folded record below; teaching the intake job (issuers, names, attestations) is its own card
 * at the foot of the page, away from the daily release.
 */

type Standing = 'ok' | 'warn' | 'bad' | 'quiet';
const DOT: Record<Standing, string> = { ok: 'text-(--ok)', warn: 'text-(--warn)', bad: 'text-(--bad)', quiet: 'text-(--muted)' };
/** The checks table's visible status: a word per standing (UI/UX audit, 31 Oct 2026, UX-15; quiet
 *  says the app's own rule-15 "Not known", never a word that could read as a result). The
 *  row's own words ("near F5172S", "not reported (optional)") kept on one line made the Status column
 *  the table's widest; they stay as the screen reader's text and the chip's title. */
const STANDING_WORD: Record<Standing, string> = { ok: 'OK', warn: 'Check', bad: 'Problem', quiet: 'Not known' };

/** One row of the checks table: what, as printed, and where it stands in three words. */
interface CheckRow {
  key: string;
  what: React.ReactNode;
  printed: React.ReactNode;
  standing: Standing;
  word: string;
  /** A second, quieter line under the standing: the reason, where there is one. */
  why?: string | null;
  /** The result's sequence when the row can be ticked. */
  tick?: number;
  /** Our spec for the line (the version judged against), when it is a spec line. */
  spec?: React.ReactNode;
  /** The line a QA comment hangs off (migration 036): 'spec-<id>' or 'result-<sequence>'. */
  lineKey?: string;
  /** Which section of the checks table it sits in: printed but not in our spec is 'extra'. */
  group?: 'extra';
  /** The line's name in words, for the controls on its row (UX-45). */
  name?: string;
}

/** The identity rows: the lot, who issued it, the product, the signature. No actions here. */
function identityRows(c: Certificate, d: ReceiptDetail): CheckRow[] {
  const field = (k: 'ISSUER' | 'PRODUCT' | 'SIGNATURE') => c.fields?.find((x) => x.field === k) ?? null;
  const supplier = d.vendor.name ?? d.vendor.account ?? 'the supplier on the order';
  const lots = c.coverage?.lots ?? [];
  const rows: CheckRow[] = [];
  if (d.batchReadError) rows.push({ key: 'lot', what: 'Batch', printed: c.lots.map((l) => l.lot).join(', ') || '—', standing: 'quiet', word: 'not known', why: 'D365 could not be read.' });
  else if (c.lots.length === 0) rows.push({ key: 'lot', what: 'Batch', printed: '—', standing: 'warn', word: 'no lot printed' });
  else if (lots.length === 0) rows.push({ key: 'lot', what: 'Batch', printed: c.lots.map((l) => l.lot).join(', '), standing: 'quiet', word: 'not known' });
  else
    for (const l of lots)
      rows.push({
        key: `lot-${l.lot}`,
        what: 'Batch',
        printed: <span className="font-mono">{l.lot}</span>,
        standing: MATCH_TONE[l.match],
        word: l.match === 'EXACT' ? 'received' : l.match === 'NEAR' ? `near ${l.batchId}` : 'not received',
        why: l.match === 'NEAR' ? l.reason : null,
      });
  const issuer = field('ISSUER');
  rows.push(
    c.fields === undefined
      ? { key: 'issuer', what: 'Issued by', printed: '—', standing: 'quiet', word: 'not known' }
      : !issuer
        ? { key: 'issuer', what: 'Issued by', printed: '—', standing: 'quiet', word: 'not recorded' }
        : issuer.match === 'EXACT' || issuer.match === 'ALIAS'
          ? { key: 'issuer', what: 'Issued by', printed: issuer.asPrinted, standing: 'ok', word: issuer.match === 'ALIAS' ? 'approved name' : 'the supplier' }
          : issuer.match === 'NEAR'
            ? { key: 'issuer', what: 'Issued by', printed: issuer.asPrinted, standing: 'warn', word: `near ${supplier}`, why: issuer.matchNote }
            : { key: 'issuer', what: 'Issued by', printed: issuer.asPrinted, standing: 'bad', word: `not ${supplier}` },
  );
  const product = field('PRODUCT');
  rows.push(
    c.fields === undefined
      ? { key: 'product', what: 'Product', printed: '—', standing: 'quiet', word: 'not known' }
      : !product
        ? { key: 'product', what: 'Product', printed: '—', standing: 'quiet', word: 'not recorded' }
        : {
            key: 'product',
            what: 'Product',
            printed: product.asPrinted,
            standing: NAME_MATCH_TONE[product.match ?? 'NONE'],
            word: (NAME_MATCH_LABEL[product.match ?? 'NONE'] ?? '').toLowerCase(),
            why: product.matchNote,
          },
  );
  const signature = field('SIGNATURE');
  rows.push(
    c.fields === undefined
      ? { key: 'signed', what: 'Signed', printed: '—', standing: 'quiet', word: 'not known' }
      : !signature || !signature.kind
        ? { key: 'signed', what: 'Signed', printed: '—', standing: 'quiet', word: 'not recorded' }
        : signature.kind === 'NONE'
          ? { key: 'signed', what: 'Signed', printed: 'nothing', standing: 'warn', word: 'not signed' }
          : {
              key: 'signed',
              what: 'Signed',
              printed: signature.asPrinted ?? SIGNATURE_LABEL[signature.kind],
              standing: c.proven?.signed ? 'ok' : 'quiet',
              word: c.proven?.signed ? 'proven' : 'not proven',
            },
  );
  return rows;
}

/** The result rows that need a person, each tickable; the ones in spec folded into one line. */
function resultRows(c: Certificate): CheckRow[] {
  const toCheck = c.results.filter((x) => x.verdict !== 'IN' && x.verdict !== 'INFO');
  const inSpec = c.results.filter((x) => x.verdict === 'IN').length;
  const rows: CheckRow[] = toCheck.map((x) => ({
    key: `result-${x.sequence}`,
    what: (
      <>
        {x.parameter}
        {x.verdictReason === NOT_IN_SPEC ? <span className="ml-(--s1) text-(--muted)">(not in spec)</span> : null}
      </>
    ),
    printed: (
      <span title={x.quote ? `Read from "${x.quote}"${x.page ? ` · p${x.page}` : ''}` : 'No quote (read by hand)'}>
        <span className="font-mono">
          {x.result}
          {x.unit && !x.result.includes(x.unit) ? ` ${x.unit}` : ''}
        </span>
        {x.printedLimit ? <span className="text-(--muted)"> · limit {x.printedLimit}</span> : null}
      </span>
    ),
    standing: x.verdict === 'OUT' ? 'bad' : x.verdict === 'MISSING' ? 'warn' : 'warn',
    word: x.verdict === 'OUT' ? 'out of spec' : x.verdict === 'MISSING' ? 'missing' : 'check',
    why: x.verdictReason && x.verdictReason !== NOT_IN_SPEC ? x.verdictReason : null,
    tick: x.sequence,
  }));
  if (c.results.length === 0) rows.push({ key: 'results', what: 'Results', printed: '—', standing: 'warn', word: 'none recorded', why: 'Read the certificate.' });
  else if (inSpec > 0) rows.push({ key: 'in-spec', what: `${inSpec} other result${inSpec === 1 ? '' : 's'}`, printed: 'in the record below', standing: 'ok', word: 'in spec' });
  return rows;
}

interface RequestDraftView {
  receiptId: number;
  kind: 'REQUEST' | 'REMINDER';
  to: string | null;
  toFrom: 'SUPPLIER_RULE' | 'LAST_SENDER' | null;
  cc: string;
  subject: string;
  body: string;
  sendOn: boolean;
  mailto: string;
}

/** "Request COA" (QA team feedback, 29 Oct 2026): the email the app writes, sent from qa@ by the app when that
 *  is switched on, else opened in Outlook; either way the chase is logged so the request stops being due. */
export function RequestCoa({ receiptId, onSent }: { receiptId: number; onSent: () => void }) {
  const [draft, setDraft] = useState<RequestDraftView | null>(null);
  const [to, setTo] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const d = await api.post<RequestDraftView>(`/receipts/${receiptId}/cofa-request`, { action: 'preview' });
      setDraft(d);
      setTo(d.to ?? '');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const act = async (action: 'send' | 'log') => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sent: boolean; to: string; remembered: boolean }>(`/receipts/${receiptId}/cofa-request`, { action, to, remember });
      setDone(`${r.sent ? `Sent to ${r.to} from qa@` : `Logged as sent to ${r.to}`}${r.remembered ? '; address saved for this supplier' : ''}.`);
      setDraft(null);
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const mailto = draft ? draft.mailto.replace(/^mailto:[^?]*/, `mailto:${encodeURIComponent(to)}`) : '';
  return (
    <div className="mt-(--s3) flex flex-col gap-(--s2)">
      {!draft ? (
        <div>
          <Button type="button" size="sm" onClick={open} disabled={busy}>
            {busy ? 'Writing the request…' : 'Request COA'}
          </Button>
        </div>
      ) : (
        <div className="flex max-w-[80ch] flex-col gap-(--s2) rounded-(--radius) border border-(--border) p-(--s3)">
          <label className="flex flex-col gap-(--s1) text-(length:--fs-sm) font-semibold">
            To
            <input
              type="email"
              className={`rounded-(--radius) border border-(--border) bg-(--card) px-(--s2) py-(--s1) font-normal ${officeFocus}`}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="supplier@example.com"
            />
          </label>
          <span className={mutedSm}>
            {draft.toFrom === 'SUPPLIER_RULE' ? 'The address saved for this supplier.' : draft.toFrom === 'LAST_SENDER' ? 'Who last sent us a certificate from this supplier.' : 'No address known for this supplier: type one.'} Cc {draft.cc}.
          </span>
          <p className="m-0 text-(length:--fs-sm) font-semibold">{draft.subject}</p>
          <pre className="m-0 whitespace-pre-wrap font-sans text-(length:--fs-sm) text-(--muted)">{draft.body}</pre>
          <label className="flex items-center gap-(--s2) text-(length:--fs-sm)">
            <input type="checkbox" className={officeFocus} checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember this address for this supplier
          </label>
          <div className="flex flex-wrap gap-(--s2)">
            {draft.sendOn ? (
              <Button type="button" size="sm" onClick={() => act('send')} disabled={busy || !to.trim()}>
                {busy ? 'Sending…' : `Send ${draft.kind === 'REMINDER' ? 'reminder' : 'request'} from qa@`}
              </Button>
            ) : (
              <>
                <a className={buttonVariants({ size: 'sm' })} href={mailto}>
                  Open in Outlook
                </a>
                <Button type="button" size="sm" variant="outline" onClick={() => act('log')} disabled={busy || !to.trim()}>
                  I sent it
                </Button>
              </>
            )}
            <Button type="button" size="sm" variant="outline" onClick={() => setDraft(null)} disabled={busy}>
              Cancel
            </Button>
          </div>
          {!draft.sendOn ? <span className={mutedSm}>Sending straight from qa@ is not switched on yet: Outlook opens the email for you to send.</span> : null}
        </div>
      )}
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--bad)">
          {error}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="m-0 text-(length:--fs-sm) font-semibold text-(--ok)">
          {done}
        </p>
      ) : null}
    </div>
  );
}

/** Our limit for a spec line, as a person reads it: "28 - 60 Units/Oz", "below 5 %", "at least 2", or the words expected. */
export function specLimitText(l: SpecLine): string {
  const u = l.unit ? ` ${l.unit}` : '';
  if (l.min !== null && l.max !== null) return `${l.min} - ${l.max}${u}`;
  if (l.max !== null) return `below ${l.max}${u}`;
  if (l.min !== null) return `at least ${l.min}${u}`;
  return l.expectedText ?? '—';
}

/** Every line of the spec the certificate was judged against, in spec order, then what was printed but is not
 *  in our spec (QA team feedback, 29 Oct 2026: "all lines of specifications in table format", not folded away). */
export function specRows(c: Certificate): CheckRow[] {
  const lines = (c.specLines ?? []).slice().sort((a, b) => a.sequence - b.sequence);
  if (!lines.length) return resultRows(c);
  const rows: CheckRow[] = lines.map((l) => {
    const r = c.results.find((x) => x.specParameterId === l.specParameterId);
    const missing = !r && (c.missing ?? []).some((m) => m.specParameterId === l.specParameterId);
    const printed = r ? (
      <span title={r.quote ? `Read from "${r.quote}"${r.page ? ` · p${r.page}` : ''}` : 'No quote (read by hand)'}>
        <span className="font-mono">
          {r.result}
          {r.unit && !r.result.includes(r.unit) ? ` ${r.unit}` : ''}
        </span>
        {r.printedLimit ? <span className="text-(--muted)"> · limit {r.printedLimit}</span> : null}
      </span>
    ) : (
      '—'
    );
    const base = { key: `spec-${l.specParameterId}`, lineKey: `spec-${l.specParameterId}`, what: l.name, name: l.name, spec: specLimitText(l), printed };
    if (!r)
      return missing
        ? { ...base, standing: 'warn' as Standing, word: 'missing', why: 'Mandatory, and not on the certificate.' }
        : { ...base, standing: 'quiet' as Standing, word: l.mandatory ? 'not reported' : 'not reported (optional)' };
    const why = r.verdictReason ? plainWords(r.verdictReason) : null;
    if (r.verdict === 'IN') return { ...base, standing: 'ok' as Standing, word: 'in spec', why };
    if (r.verdict === 'OUT') return { ...base, standing: 'bad' as Standing, word: 'out of spec', why, tick: r.sequence };
    return { ...base, standing: 'warn' as Standing, word: 'check', why, tick: r.sequence };
  });
  for (const r of c.results.filter((x) => !x.specParameterId || x.verdict === 'INFO')) {
    rows.push({
      key: `result-${r.sequence}`,
      lineKey: `result-${r.sequence}`,
      what: r.parameter,
      name: r.parameter,
      spec: 'not in our spec',
      printed: (
        <span className="font-mono">
          {r.result}
          {r.unit && !r.result.includes(r.unit) ? ` ${r.unit}` : ''}
        </span>
      ),
      standing: 'quiet',
      word: 'not judged',
      why: r.verdictReason && r.verdictReason !== NOT_IN_SPEC ? plainWords(r.verdictReason) : null,
      group: 'extra',
    });
  }
  if (!c.results.length && !(c.missing ?? []).length) rows.push({ key: 'results', what: 'Results', printed: '—', standing: 'warn', word: 'none recorded', why: 'Read the certificate.' });
  return rows;
}

/** The status, readable without colour: an icon of its own shape and a word (red, amber, green; grey = not known). */
const STATUS_ICON: Record<Standing, typeof CircleCheck> = { ok: CircleCheck, warn: TriangleAlert, bad: CircleX, quiet: CircleMinus };

/** QA's note on one line (migration 036): the newest shown; Add or Edit writes a new one. */
function CommentCell({ c, lineKey, name, onCommented }: { c: Certificate; lineKey: string; name: string; onCommented: () => void }) {
  const current = (c.comments ?? []).find((x) => x.lineKey === lineKey) ?? null;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(current?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!editing)
    return (
      <span className="flex items-start gap-(--s1)">
        {current ? (
          <span className="max-w-[24ch] break-words" title={`${current.byUpn}${current.at ? ` · ${stamp(current.at)}` : ''}`}>
            {current.comment}
          </span>
        ) : null}
        {/* Named by its row, and at least 24px to hit (UI/UX audit, 31 Oct 2026, UX-45): twenty-three
            "Add comment" buttons, 23x18 each, were one name to a screen reader. */}
        <button
          type="button"
          className={`inline-flex min-h-[24px] min-w-[24px] shrink-0 items-center justify-center px-(--s1) text-(length:--fs-xs) font-semibold text-(--primary) underline-offset-2 hover:underline ${officeFocus}`}
          aria-label={`${current ? 'Edit' : 'Add'} comment on ${name}`}
          onClick={() => {
            setText(current?.comment ?? '');
            setEditing(true);
          }}
        >
          {current ? 'Edit' : 'Add'}
        </button>
      </span>
    );
  return (
    <form
      className="flex flex-col gap-(--s1)"
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
          await api.post(`/cofas/${c.cofaId}/comments`, { lineKey, comment: text });
          setEditing(false);
          onCommented();
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setSaving(false);
        }
      }}
    >
      <textarea
        aria-label={`Comment on ${name}`}
        className={`min-h-[56px] w-full min-w-[24ch] rounded-(--radius) border border-(--border) bg-(--card) p-(--s1) text-(length:--fs-sm) ${officeFocus}`}
        maxLength={500}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {error ? (
        <span role="alert" className="text-(length:--fs-xs) text-(--bad)">
          {error}
        </span>
      ) : null}
      <span className="flex gap-(--s1)">
        <Button type="submit" size="sm" disabled={saving || !text.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </span>
    </form>
  );
}

/** The checks table on the batch card (Amit, 27 Oct 2026: "there has to be a cleaner way"). One row per
 *  thing a person releasing the batch asks about, with where it stands as an icon and a word, and one
 *  Checked column for the results. A real table, so the columns line up and a screen reader gets the
 *  headers - deliberately not DataTable or AG Grid (29 Oct 2026 review): spec order is the point, so
 *  no sort or search, and the sections below need one set of columns across them.
 *  Three sections, each a <tbody> with its own header row: the goods and the certificate, our
 *  specification in spec order, then what was printed but is not in our spec. Styled as the registers
 *  (`data-table`: zebra, hover, the status as an inset bar - red for out, amber for a check); the
 *  table grows with the page rather than scrolling inside it (UX-15, 31 Oct 2026), so the header row
 *  scrolls with it; below `sm` each row is a card (`stack-sm`, index.css). */
function ChecksTable({ c, d, checked, onToggle, onCommented }: { c: Certificate; d: ReceiptDetail; checked: ReadonlySet<number>; onToggle: (sequence: number) => void; onCommented: () => void }) {
  const goods = identityRows(c, d);
  const all = specRows(c);
  const spec = all.filter((r) => r.group !== 'extra');
  const extra = all.filter((r) => r.group === 'extra');
  const ticks = [...goods, ...all].filter((r) => r.tick !== undefined).length;
  const sections = [
    { id: 'goods', label: 'The goods and the certificate', rows: goods },
    { id: 'spec', label: c.specLines?.length ? `Our specification (${c.specLines.length} line${c.specLines.length === 1 ? '' : 's'})` : 'Results', rows: spec },
    { id: 'extra', label: `Also printed, not in our spec (${extra.length})`, rows: extra },
  ].filter((x) => x.rows.length > 0);
  const head = 'sticky top-0 z-1 whitespace-nowrap border-b border-(--border-strong) bg-(--subtle) px-[12px] py-[9px] text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-(--muted)';
  const cell = 'border-b border-(--border) px-[12px] py-[8px] align-top';
  const COLUMNS = ['Status', 'Check', 'Our spec', 'On the certificate', 'Reason', 'Comment'];
  return (
    // No height cap (UI/UX audit, 31 Oct 2026, UX-15): inside a page that scrolls, a 75vh box scrolled
    // both ways at once. The table is sized to fit an iPad's content column (~650px): one-word status,
    // Reason and Comment wrap. It still scrolls sideways rather than squash below that.
    <div className="mt-(--s3) overflow-x-auto rounded-(--radius) border border-(--border) bg-(--card) shadow-card">
      <table
        aria-label={`Certificate ${c.cofaId} against the specification`}
        className="data-table stack-sm w-full border-separate border-spacing-0 text-(length:--fs-sm) sm:min-w-[640px]"
      >
        <thead>
          <tr>
            {COLUMNS.map((h) => (
              <th key={h} scope="col" className={head}>
                {h}
              </th>
            ))}
            <th scope="col" className={`${head} text-right`}>
              {ticks ? 'Checked' : ''}
            </th>
          </tr>
        </thead>
        {sections.map((sec) => (
          <tbody key={sec.id}>
            <tr data-section="true">
              <th scope="colgroup" colSpan={7} className="border-b border-(--border) bg-(--card) px-[12px] pb-[6px] pt-[14px] text-left text-(length:--fs-xs) font-semibold uppercase tracking-[0.06em] text-(--muted)">
                {sec.label}
              </th>
            </tr>
            {sec.rows.map((r) => {
              const Icon = STATUS_ICON[r.standing];
              return (
                <tr key={r.key} data-accent={r.standing === 'bad' ? 'bad' : r.standing === 'warn' ? 'warn' : undefined}>
                  <td data-label="Status" className={cell}>
                    <span className={`inline-flex items-center gap-(--s1) whitespace-nowrap font-semibold ${DOT[r.standing]}`}>
                      <Icon aria-hidden="true" className="size-[18px] shrink-0" strokeWidth={2.25} />
                      <span aria-hidden="true" className={r.standing === 'quiet' ? 'text-(--muted)' : 'text-(--fg)'}>
                        {STANDING_WORD[r.standing]}
                      </span>
                    </span>
                    {/* The row's own words under the chip, visible and allowed to wrap (issue #156,
                        31 Oct 2026): as a title only, an iPad - no hover - lost "near F5172S".
                        Small and wrapping, so the Status column stays narrow (UX-15). */}
                    <span className="block max-w-[16ch] break-words text-(length:--fs-xs) text-(--muted)">{r.word}</span>
                  </td>
                  <th scope="row" data-label="Check" className={`${cell} text-left font-semibold`}>
                    {r.what}
                  </th>
                  <td data-label="Our spec" className={cell}>
                    {r.spec ?? '—'}
                  </td>
                  <td data-label="On the certificate" className={cell}>
                    {r.printed}
                  </td>
                  <td data-label="Reason" className={`${cell} max-w-[28ch] break-words text-(length:--fs-xs) text-(--muted)`}>
                    {r.why ?? ''}
                  </td>
                  <td data-label="Comment" className={cell}>
                    {r.lineKey ? <CommentCell c={c} lineKey={r.lineKey} name={r.name ?? 'this line'} onCommented={onCommented} /> : null}
                  </td>
                  <td data-label={r.tick !== undefined ? 'Checked' : ''} className={`${cell} text-right`}>
                    {r.tick !== undefined ? (
                      <input
                        type="checkbox"
                        className={`size-[18px] ${officeFocus}`}
                        aria-label={`Checked on the certificate: ${c.results.find((x) => x.sequence === r.tick)?.parameter ?? ''}`}
                        checked={checked.has(r.tick)}
                        onChange={() => onToggle(r.tick!)}
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/** The note the ticks write: what was checked, said once per result, in the order printed. */
export function checkedNote(c: Pick<Certificate, 'results'>, checked: ReadonlySet<number>): string {
  const lines = c.results
    .filter((x) => checked.has(x.sequence))
    .map((x) => `${x.parameter} ${x.result}${x.unit && !x.result.includes(x.unit) ? ` ${x.unit}` : ''}${x.printedLimit ? ` (limit ${x.printedLimit})` : ''}`);
  return lines.length ? `Checked on the certificate: ${lines.join('; ')}.` : '';
}

/** The required checks no certificate for the batch reports - once for the batch, not once per file. */
function MissingAcross({ group }: { group: Certificate[] }) {
  const label = 'Still missing across the certificates';
  if (!judgedAgainstSpec(group)) return <p className={`${mutedSm} mt-(--s2)`}>{label}: not known (no spec to judge against).</p>;
  const missing = missingAcross(group);
  if (missing === null) {
    return <p className={`${mutedSm} mt-(--s2)`}>{label}: not known (a certificate's list of required checks did not arrive).</p>;
  }
  if (missing.length === 0) return <p className={`${mutedSm} mt-(--s2)`}>Every required check is reported on at least one certificate.</p>;
  return (
    <p className="m-0 mt-(--s2) max-w-[100ch] text-(length:--fs-sm)">
      <span className="font-semibold text-(--warn)">{label}: </span>
      {missing.map((m, i) => (
        <span key={m.parameter}>
          {i ? ', ' : ''}
          {m.parameter}
          {m.perhaps.length ? (
            <span className="text-(--muted)"> (perhaps {m.perhaps.map((p) => `“${p.parameter}” on #${p.cofaId}`).join(', ')})</span>
          ) : null}
        </span>
      ))}
      .
    </p>
  );
}

/** Asks D365 again for this line (the register's Refresh, narrowed to the line's due day), then
 *  reads the line again. A failure is said, never shown as "nothing changed". */
function ReadD365Again({ expectedDate, onRead }: { expectedDate: string | null; onRead: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function read() {
    setBusy(true);
    setError(null);
    try {
      const day = expectedDate?.slice(0, 10);
      await api.post('/receipt-sync', day ? { from: day, to: day } : {});
      onRead();
    } catch (err: unknown) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-(--s2) flex flex-wrap items-center gap-(--s2)">
      <Button variant="outline" type="button" onClick={() => void read()} disabled={busy}>
        {busy ? 'Reading D365…' : 'Read D365 again'}
      </Button>
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--bad)">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** A certificate's chips: its verdict, the spec's status, how it came in, where the decision stands. */
function CertificateHead({ c }: { c: Certificate }) {
  const latest = c.decisions[c.decisions.length - 1] ?? null;
  return (
    <div className="flex flex-wrap items-center gap-x-(--s2) gap-y-(--s1)">
      <h3 className={`${h3Class} mr-(--s1)`}>Certificate #{c.cofaId}</h3>
      <VerdictBadge verdict={c.verdict ?? null} />
      {c.verdictReasons?.spec?.status === 'DRAFT' ? <span className={`${chipClass} ${TONE_CLASS.warn}`}>Draft spec</span> : null}
      {c.door === 'SYSTEM' ? <span className={`${chipClass} ${TONE_CLASS.quiet}`}>Recorded automatically</span> : null}
      <span className={`${chipClass} ${latest ? TONE_CLASS[latest.decision === 'APPROVED' ? 'ok' : 'bad'] : TONE_CLASS.quiet}`}>
        {latest ? DECISION_LABEL[latest.decision] : 'Not decided'}
      </span>
      {c.supersededBy === null ? null : <span className={`${chipClass} ${TONE_CLASS.quiet}`}>Superseded by #{c.supersededBy}</span>}
    </div>
  );
}

/** Everything about one certificate that is not the decision: its lots, the server's verdict and
 *  reasons, every result, identity and signature (with the optional attest and approve forms), where it
 *  came from and its decision history. A live certificate that is not the one a batch's form decides
 *  gets its own form here, so every live certificate can still be decided. */
function CertificateDetails({
  c,
  decideHere,
  checklistAbove,
  batchReadError,
  grants,
  onDecided,
  configs,
}: {
  c: Certificate;
  decideHere: boolean;
  /** The card's checklist already lists this certificate's results to check: do not list them twice. */
  checklistAbove: boolean;
  batchReadError: string | null;
  grants: MyGrants;
  onDecided: () => void;
  /** The NCR and QC Hold switches, for the Reject hint (UX-49). Absent: not known. */
  configs?: DecisionConfigs;
}) {
  const live = c.supersededBy === null;
  const reasons = (c.verdictReasons?.reasons ?? []).map(plainWords);
  return (
    <>
      <p className={`${mutedSm} mt-(--s1) flex flex-wrap items-center gap-x-(--s2) gap-y-(--s1)`}>
        <span>{CERTIFICATE_TYPE_LABEL[c.certificateType]}</span>
        <span>· {c.issuer ?? 'issuer not recorded'}</span>
        {c.issuedDate ? <span>· issued {shortDay(c.issuedDate)}</span> : null}
        <span>·</span>
        {c.lots.length === 0 ? (
          <span className="font-semibold text-(--warn)">no lot recorded</span>
        ) : (
          c.lots.map((l) => {
            const cov = c.coverage?.lots.find((x) => x.lot === l.lot);
            const match = cov?.match ?? 'NONE';
            return (
              <span key={l.lot} className={`${chipClass} ${TONE_CLASS[MATCH_TONE[match]]}`}>
                lot <span className="ml-[0.4ch] font-mono">{l.lot}</span>
                {cov?.batchId && match !== 'EXACT' ? ` ≈ ${cov.batchId}` : ''}
                {batchReadError
                  ? ' · batches unknown'
                  : !c.coverage?.received
                    ? ' · not yet receipted'
                    : match === 'NONE'
                      ? ' · matches no batch'
                      : ''}
              </span>
            );
          })
        )}
      </p>

      {c.verdict ? (
        <div className="mt-(--s3) text-(length:--fs-sm)">
          <p className="m-0 font-semibold">{verdictText(c.verdict, c.decisions.length > 0)}</p>
          {reasons.length ? (
            <ul className="m-0 mt-(--s1) max-w-[80ch] pl-(--s4)">
              {reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <p className={`${mutedSm} mt-(--s3)`}>Recorded before certificates were judged: nothing compared it to a spec.</p>
      )}

      <Results c={c} toCheck={!checklistAbove} />

      <div className="mt-(--s3) flex flex-col gap-(--s1) text-(length:--fs-sm)">
        <p className="m-0">
          {SOURCE_LABEL[c.sourceKind]}
          {c.sourceKind === 'EMAIL'
            ? ` in ${c.source.mailbox ?? '—'}: "${c.source.subject ?? '(no subject)'}" from ${c.source.senderAddress ?? '—'}, ${stamp(c.source.emailReceivedAt)}${c.source.attachmentName ? ` · ${c.source.attachmentName}` : ''}`
            : ''}
          {c.certificateNumber ? ` · no. ${c.certificateNumber}` : ''}
        </p>
        <p className="m-0 text-(--muted)">
          {/* In words, not "found by BATCH" (UI/UX audit, 31 Oct 2026, UX-18). */}
          {READ_MODE_LABEL[c.readMode]} · matched by {foundByLabel(c.foundBy)} · recorded{' '}
          {c.door === 'SYSTEM' ? (
            <strong className="font-semibold text-(--fg)">{recordedVia(c.door)}</strong>
          ) : (
            `by ${c.recordedByUpn}`
          )}{' '}
          on {stamp(c.recordedAt)}
          {c.door === 'SYSTEM' ? '' : ` ${recordedVia(c.door)}`}
          {c.supersedes ? ` · corrects #${c.supersedes}` : ''}
        </p>
        {c.lots.map((l) => (
          <p key={l.lot} className="m-0 text-(--muted)">
            Lot <span className="font-mono">{l.lot}</span>{' '}
            {l.quote ? `read from "${l.quote}"${l.page ? ` · p${l.page}` : ''}` : 'no quote (read by hand)'}
          </p>
        ))}
        {c.notes ? <p className="m-0 max-w-[80ch] text-(--muted)">{plainWords(c.notes)}</p> : null}
      </div>

      {c.decisions.length > 0 ? (
        <ul className="m-0 mt-(--s3) list-none p-0 text-(length:--fs-sm)">
          {c.decisions.map((x) => (
            <li key={x.decisionId}>
              {x.automatic ? (
                <>
                  <strong>{AUTOMATIC_APPROVAL}</strong> {AUTOMATIC_APPROVAL_WHY}, on {stamp(x.decidedAt)}. A person can still reject it or ask for a new certificate: the latest decision wins.
                </>
              ) : (
                <>
                  <strong>{DECISION_LABEL[x.decision]}</strong> by {x.decidedByUpn} on {stamp(x.decidedAt)}
                </>
              )}
              {x.ncrId ? ` · NCR ${x.ncrId}` : ''}
              {x.note ? ` · ${x.note}` : ''}
            </li>
          ))}
        </ul>
      ) : null}

      {live && decideHere ? <DecisionPanel certificate={c} batchReadError={batchReadError} grants={grants} onDecided={onDecided} configs={configs} /> : null}
    </>
  );
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** Results that need a person first, each with its one-line reason; the ones in spec, and those printed
 *  but not in our spec (INFO, Amit 24 Oct 2026 - never judged, never a stop), folded away. */
function Results({ c, toCheck: listToCheck }: { c: Certificate; toCheck: boolean }) {
  if (c.results.length === 0) return <p className={`${mutedSm} mt-(--s3)`}>No results recorded.</p>;
  const toCheck = listToCheck ? c.results.filter((x) => x.verdict !== 'IN' && x.verdict !== 'INFO') : [];
  const inSpec = c.results.filter((x) => x.verdict === 'IN');
  const alsoPrinted = c.results.filter((x) => x.verdict === 'INFO');
  return (
    <div className="mt-(--s3)">
      {toCheck.length ? (
        <DataTable
          data={toCheck}
          columns={resultColumns}
          caption={`Results on certificate ${c.cofaId} to check`}
          getRowId={(x) => String(x.sequence)}
          rowAccent={(x) => (x.verdict === 'OUT' ? 'bad' : null)}
          maxHeight="24rem"
        />
      ) : null}
      {inSpec.length ? (
        <details className="mt-(--s2)">
          <summary className={summaryClass}>{plural(inSpec.length, 'result')} in spec</summary>
          <div className="mt-(--s1)">
            <DataTable
              data={inSpec}
              columns={resultColumns}
              caption={`Results on certificate ${c.cofaId} in spec`}
              getRowId={(x) => String(x.sequence)}
              maxHeight="24rem"
            />
          </div>
        </details>
      ) : null}
      {alsoPrinted.length ? (
        <details className="mt-(--s2)">
          <summary className={summaryClass}>Also printed (not in our spec): {plural(alsoPrinted.length, 'result')}</summary>
          <div className="mt-(--s1)">
            <DataTable
              data={alsoPrinted}
              columns={resultColumns}
              caption={`Results on certificate ${c.cofaId} also printed, not in our spec`}
              getRowId={(x) => String(x.sequence)}
              maxHeight="24rem"
            />
          </div>
        </details>
      ) : null}
    </div>
  );
}

type Form = AttestationKind | 'ISSUER' | 'PRODUCT';

/** Proven, not proven, attested — or "Not known" when the server computed nothing (rule 15). */
function ProofChip({ proven, attestedBy }: { proven: boolean | null; attestedBy: string | null }) {
  if (attestedBy) return <span className={`${chipClass} ${TONE_CLASS.ok}`}>Attested by {attestedBy}</span>;
  if (proven === null) return <span className={`${chipClass} ${TONE_CLASS.quiet}`}>Not known</span>;
  return <span className={`${chipClass} ${TONE_CLASS[proven ? 'ok' : 'warn']}`}>{proven ? 'Proven' : 'Not proven'}</span>;
}

/** One line each for identity and signature; the attest and approve forms open only when asked for.
 *  Hidden for whoever recorded the certificate: the server refuses them whatever the screen shows. */
function IdentityLines({
  c,
  live,
  recorder,
  attested,
  grants,
  onDone,
}: {
  c: Certificate;
  live: boolean;
  recorder: boolean;
  attested: Set<AttestationKind>;
  grants: MyGrants;
  onDone: () => void;
}) {
  const [form, setForm] = useState<Form | null>(null);
  const [note, setNote] = useState('');
  const [issuerKind, setIssuerKind] = useState<IssuerKind>('MANUFACTURER');
  const [scope, setScope] = useState<'ITEM' | 'VENDOR'>('ITEM');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const fields = c.fields;
  const field = (k: 'ISSUER' | 'PRODUCT' | 'SIGNATURE') => fields?.find((x) => x.field === k) ?? null;
  const approvable = (k: 'ISSUER' | 'PRODUCT') => {
    const f = field(k);
    return f && f.quoteVerified && (f.match === 'NEAR' || f.match === 'NONE') ? f : null;
  };
  const issuer = approvable('ISSUER');
  const product = approvable('PRODUCT');
  const signature = field('SIGNATURE');
  const signatureNone = signature?.kind === 'NONE';
  const canAttest = (k: AttestationKind) =>
    live && !recorder && !attested.has(k) && !(k === 'IDENTITY' ? c.proven?.identity : c.proven?.signed) && !(k === 'SIGNATURE' && signatureNone);
  const attestedBy = (k: AttestationKind) => (c.attestations ?? []).find((a) => a.kind === k)?.attestedByUpn ?? null;
  const proven = (v: boolean | undefined) => (c.proven ? (v ?? null) : null);

  const attestHint =
    grantHint(grants, 'CAPTURE', 'Attesting a certificate') ?? (visibleText(note) ? null : 'Say what you checked, and against what.');
  const approveHint = grantHint(grants, 'SPEC_APPROVE', 'Approving a name');

  async function send(path: string, body: unknown) {
    setBusy(true);
    setRefusal(null);
    try {
      await api.post(path, body);
      setNote('');
      setForm(null);
      onDone();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const named = (k: 'ISSUER' | 'PRODUCT') => {
    const f = field(k);
    if (!f) return <span className="text-(--warn)">not recorded</span>;
    return (
      <span>
        “{f.asPrinted}”{' '}
        {f.match ? <span className={`${chipClass} ${TONE_CLASS[NAME_MATCH_TONE[f.match]]}`}>{NAME_MATCH_LABEL[f.match]}{f.matchNote ? ` · ${f.matchNote}` : ''}</span> : null}{' '}
        <span className="text-(length:--fs-xs) text-(--muted)">{inFile(f)}</span>
      </span>
    );
  };
  const toggle = (f: Form, label: string) => (
    <Button type="button" variant="outline" size="sm" onClick={() => setForm(form === f ? null : f)} aria-expanded={form === f}>
      {label}
    </Button>
  );
  const rowClass = 'flex flex-wrap items-center gap-x-(--s2) gap-y-(--s1) text-(length:--fs-sm)';

  return (
    <div className="mt-(--s3) flex flex-col gap-(--s2)">
      {fields === undefined ? (
        <p className={mutedSm}>Identity and signature: not known (the server did not send them).</p>
      ) : (
        <>
          <div className={rowClass}>
            <strong>Identity</strong>
            <ProofChip proven={proven(c.proven?.identity)} attestedBy={attestedBy('IDENTITY')} />
            <span>issuer {named('ISSUER')}</span>
            <span>· product {named('PRODUCT')}</span>
            {canAttest('IDENTITY') ? toggle('IDENTITY', 'Attest identity') : null}
            {live && !recorder && issuer ? toggle('ISSUER', 'Approve this issuer for future certificates') : null}
            {live && !recorder && product ? toggle('PRODUCT', 'Approve this product name for future certificates') : null}
          </div>
          <div className={rowClass}>
            <strong>Signature</strong>
            <ProofChip proven={signatureNone ? false : proven(c.proven?.signed)} attestedBy={attestedBy('SIGNATURE')} />
            {signature ? (
              <span>
                {signature.kind ? SIGNATURE_LABEL[signature.kind] : null}
                {signature.asPrinted ? ` — ${signature.asPrinted}` : ''}
                {signature.role ? `, ${signature.role}` : ''}{' '}
                <span className="text-(length:--fs-xs) text-(--muted)">{inFile(signature)}</span>
              </span>
            ) : (
              <span className="text-(--warn)">not recorded</span>
            )}
            {canAttest('SIGNATURE') ? toggle('SIGNATURE', 'Attest signature') : null}
          </div>
        </>
      )}
      {live && recorder ? (
        <p className={mutedSm}>You recorded this certificate, so attesting it or approving a name from it is for somebody else.</p>
      ) : null}
      {c.attestations?.length ? (
        <ul className="m-0 list-none p-0 text-(length:--fs-xs) text-(--muted)">
          {c.attestations.map((a) => (
            <li key={a.attestationId}>
              {ATTESTATION_LABEL[a.kind]} attested by {a.attestedByUpn} on {stamp(a.attestedAt)}: {a.note} · recorded again as #{a.resultingCofaId}
            </li>
          ))}
        </ul>
      ) : null}

      {form === 'IDENTITY' || form === 'SIGNATURE' ? (
        <div className="rounded-(--radius) border border-(--border) p-(--s3)">
          <p className={`${mutedSm} max-w-[80ch]`}>
            Where the machine cannot prove it — an image signature, a name the reader could not confirm — you can stand behind it after
            checking the file. It is recorded again as a new certificate with the verdict it now earns; this one keeps its own.
          </p>
          <label className="mt-(--s2) flex flex-col gap-(--s1)">
            <span className={labelClass}>What you checked</span>
            <textarea
              className={`${fieldClass} ${officeFocus} min-h-[64px] py-(--s2)`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
            />
          </label>
          <div className="mt-(--s2) flex flex-wrap items-center gap-(--s2)">
            <span title={attestHint ?? `Attest this certificate's ${ATTESTATION_LABEL[form]}`}>
              <Button
                type="button"
                disabled={busy || attestHint !== null}
                onClick={() => void send(`/cofas/${c.cofaId}/attest`, { kind: form, note: note.trim() })}
              >
                Record {ATTESTATION_LABEL[form]} attestation
              </Button>
            </span>
            {attestHint ? <span className="text-(length:--fs-xs) text-(--muted)">{attestHint}</span> : null}
          </div>
        </div>
      ) : null}

      {(form === 'ISSUER' && issuer) || (form === 'PRODUCT' && product) ? (
        <div className="rounded-(--radius) border border-(--border) p-(--s3)">
          <div className="flex flex-wrap items-end gap-(--s2)">
            {form === 'ISSUER' && issuer ? (
              <>
                <label className="flex flex-col gap-(--s1)">
                  <span className={labelClass}>Issuer kind</span>
                  <select
                    className={`${fieldClass} ${officeFocus}`}
                    value={issuerKind}
                    onChange={(e) => {
                      const k = e.target.value as IssuerKind;
                      setIssuerKind(k);
                      // Q6 (Anna, 13 Oct 2026): only a laboratory may be approved for the whole supplier;
                      // the server refuses any other kind for it. A laboratory defaults to that.
                      setScope(k === 'LABORATORY' ? 'VENDOR' : 'ITEM');
                    }}
                  >
                    {(Object.keys(ISSUER_KIND_LABEL) as IssuerKind[]).map((k) => (
                      <option key={k} value={k}>
                        {ISSUER_KIND_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-(--s1)">
                  <span className={labelClass}>For</span>
                  <select className={`${fieldClass} ${officeFocus}`} value={scope} onChange={(e) => setScope(e.target.value as 'ITEM' | 'VENDOR')}>
                    <option value="ITEM">this item only</option>
                    <option value="VENDOR" disabled={issuerKind !== 'LABORATORY'}>
                      anything from this supplier
                    </option>
                  </select>
                </label>
                <span title={approveHint ?? 'Approve this issuer'}>
                  <Button
                    type="button"
                    disabled={busy || approveHint !== null}
                    onClick={() => void send('/supplier-issuers', { cofaId: c.cofaId, kind: issuerKind, scope })}
                  >
                    Approve “{issuer.asPrinted}” as an issuer
                  </Button>
                </span>
              </>
            ) : product ? (
              <span title={approveHint ?? 'Approve this name'}>
                <Button type="button" disabled={busy || approveHint !== null} onClick={() => void send('/material-aliases', { cofaId: c.cofaId })}>
                  Approve “{product.asPrinted}” as a name of this material
                </Button>
              </span>
            ) : null}
          </div>
          <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-xs) text-(--muted)">
            {approveHint ? `${approveHint} ` : ''}An approval changes no verdict already recorded: the next record of this certificate is
            judged with it.
          </p>
        </div>
      ) : null}

      {refusal ? (
        <p role="alert" className={`m-0 rounded-(--radius) border p-(--s3) text-(length:--fs-sm) font-semibold ${TONE_CLASS.warn}`}>
          {refusal}
        </p>
      ) : null}
    </div>
  );
}

/** Whether the server found a field's words in the file: true, or not shown to be (never "false", rule 15). */
function inFile(f: { quote: string | null; quoteVerified: boolean | null }): string {
  return f.quoteVerified ? 'found in the file' : f.quote ? 'not confirmed in the file' : 'no words to find';
}

/** The NCRs a rejection raised in D365 (the decision route's `ncr`, 29 Oct 2026). */
export interface RejectNcr {
  raised: Array<{ batch: string | null; ncrId: string }>;
  existing?: Array<{ batch: string | null; ncrId: string }>;
  refused: Array<{ batch: string | null; reason: string; message: string }>;
}

/** One sentence for the person: what was raised, what already stood, what was not and why. Null: no rejection. */
export function rejectNcrSentence(n: RejectNcr | null): string | null {
  if (!n) return null;
  const at = (b: string | null) => (b ? ` for batch ${b}` : '');
  const parts = [
    ...n.raised.map((r) => `NCR ${r.ncrId} raised in D365${at(r.batch)}.`),
    ...(n.existing ?? []).map((r) => `NCR ${r.ncrId} already raised${at(r.batch)}.`),
    ...n.refused.map((r) => `No NCR${at(r.batch)}: ${r.message}`),
  ];
  return parts.length ? parts.join(' ') : null;
}

/** Said beside the Record button until a decision is chosen (UX-01). */
const CHOOSE_DECISION = 'Choose Approve, Request a new certificate or Reject.';

/** The app's NCR and QC Hold switches (rules 29, 30), as GET /ncrs/config and /batch-holds/config said.
 *  null: the read failed or has not landed - not known, never "off". */
export interface DecisionConfigs {
  ncr: NcrConfig | null;
  hold: HoldConfig | null;
}

/** What Reject leads to, from the switches as they stand (UI/UX audit, 31 Oct 2026, UX-49: the hint said
 *  "as today" whatever the app was set to do). With both off it is the sentence it always was. Rule 15:
 *  a switch that could not be read is said to be not known, never taken as off - an NCR raised by hand
 *  beside one the app raised is a duplicate in D365. */
export function rejectHint(configs: DecisionConfigs | undefined): string {
  const ncr = configs?.ncr ?? null;
  const hold = configs?.hold ?? null;
  if (ncr && hold && !ncr.enabled && !hold.enabled) return 'Raise the NCR and quarantine order in D365 as today.';
  const ncrPart = !ncr
    ? 'Whether recording it raises an NCR in D365 is not known (reload the page before raising one there).'
    : ncr.enabled
      ? 'Recording it raises an NCR in D365 for each batch, as you.'
      : 'Raise the NCR in D365 as today.';
  const holdPart = !hold
    ? 'Whether QC Hold is asked for from this card is not known.'
    : hold.enabled
      ? 'Then put the batch on QC Hold from this card.'
      : 'Raise the quarantine order in D365 as today.';
  return `${ncrPart} ${holdPart}`;
}

function DecisionPanel({
  certificate,
  batchReadError,
  grants,
  onDecided,
  suggestedNote,
  configs,
}: {
  certificate: Certificate;
  batchReadError: string | null;
  grants: MyGrants;
  onDecided: () => void;
  /** What the card's ticks say was checked: pre-fills the note until the person writes their own. */
  suggestedNote?: string;
  /** The NCR and QC Hold switches, for the Reject hint (UX-49). Absent: not known. */
  configs?: DecisionConfigs;
}) {
  // Nothing is chosen until the person chooses (UI/UX audit, 31 Oct 2026, UX-01): a form that opened
  // on Approve recorded an approval in one click on a line reading "Needs review". No note is asked of
  // a NEEDS_REVIEW approval for it (Amit, 2 Oct 2026); the choice itself is the safeguard.
  const [decision, setDecision] = useState<CofaDecision | null>(null);
  const [note, setNote] = useState('');
  const [suggested, setSuggested] = useState('');
  useEffect(() => {
    if (suggestedNote === undefined) return;
    setNote((n) => (n === '' || n === suggested ? suggestedNote : n));
    setSuggested(suggestedNote);
    // `suggested` is read on purpose only when the suggestion changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedNote]);
  const [ncrId, setNcrId] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const blocker = (withNote: string) =>
    decision === null
      ? CHOOSE_DECISION
      : decisionBlocker(
          decision,
          certificate.coverage,
          batchReadError,
          withNote,
          certificate.failedOn ?? (certificate.verdict === 'FAIL' ? certificate.cofaId : null),
          // The server also reads earlier versions for a NONE; a list that did not arrive claims nothing.
          certificate.fields === undefined
            ? null
            : certificate.fields.some((f) => f.field === 'SIGNATURE' && f.kind === 'NONE')
              ? 'NONE'
              : certificate.fields.some((f) => f.field === 'SIGNATURE')
                ? null
                : 'UNRECORDED',
        );
  const hint = grantHint(grants, 'RELEASE', 'Deciding a certificate') ?? blocker(note);
  // Said beside the note before anything is typed: the reason a note is required, when only a note
  // clears the blocker (the server's own rule, mirrored in decisionBlocker - not re-derived here). A
  // case it does not know is the server's refusal, shown on submit.
  const empty = blocker('');
  const noteRequired = empty !== null && blocker('x') === null ? empty : null;
  const latest = certificate.decisions[certificate.decisions.length - 1] ?? null;
  const name = `cofa-decision-${certificate.cofaId}`;
  // What D365 did with the NCR a rejection raises (Amit, 29 Oct 2026): said once, after the decision.
  const [ncrResult, setNcrResult] = useState<string | null>(null);
  const hintId = `decision-hint-${certificate.cofaId}`;
  // A decided certificate keeps its form folded behind "Change decision on #N" (UI/UX audit, 31 Oct 2026,
  // UX-25: three open forms sat under an approved PO). Nothing about deciding changes once it is open:
  // the same choice, note and blocker, and the server's latest-decision-wins (rule 18).
  const [changing, setChanging] = useState(false);
  const folded = latest !== null && !changing;

  async function submit() {
    if (decision === null) return;
    setBusy(true);
    setRefusal(null);
    setNcrResult(null);
    try {
      const res = await api.post<{ ncr?: RejectNcr | null }>(`/cofas/${certificate.cofaId}/decision`, {
        decision,
        note: note.trim() || undefined,
        ncrId: ncrId.trim() || undefined,
      });
      setNcrResult(rejectNcrSentence(res?.ncr ?? null));
      setDecision(null);
      setNote('');
      setNcrId('');
      setChanging(false);
      onDecided();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-(--s3) border-t border-(--border-strong) pt-(--s3)">
      <h3 className="m-0 text-(length:--fs-md) font-semibold">Decide certificate #{certificate.cofaId}</h3>
      {ncrResult ? (
        <p role="status" className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) font-semibold">
          {ncrResult}
        </p>
      ) : null}
      {latest ? (
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
          Currently {latest.automatic ? `${AUTOMATIC_APPROVAL.toLowerCase()} ${AUTOMATIC_APPROVAL_WHY}` : `${DECISION_LABEL[latest.decision].toLowerCase()} by ${latest.decidedByUpn}`} on{' '}
          {stamp(latest.decidedAt)}.{folded ? '' : ' A new decision is added to the history.'}
        </p>
      ) : null}
      {folded ? (
        <div className="mt-(--s2)">
          <Button type="button" variant="outline" size="sm" aria-expanded={false} onClick={() => setChanging(true)}>
            Change decision on #{certificate.cofaId}
          </Button>
        </div>
      ) : (
        <>
          <fieldset className="m-0 mt-(--s2) flex flex-wrap gap-(--s3) border-0 p-0">
            <legend className={labelClass}>Decision</legend>
            {(['APPROVED', 'NEW_COFA_REQUESTED', 'REJECTED'] as const).map((opt) => (
              <label key={opt} className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
                <input
                  type="radio"
                  name={name}
                  className={officeFocus}
                  checked={decision === opt}
                  onChange={() => setDecision(opt)}
                />
                {opt === 'APPROVED' ? 'Approve' : opt === 'REJECTED' ? 'Reject' : 'Request a new certificate'}
              </label>
            ))}
          </fieldset>

          <label className="mt-(--s2) flex flex-col gap-(--s1)">
            <span className={labelClass}>
              Note
              {noteRequired ? (
                <span className="ml-(--s2) font-normal normal-case tracking-normal text-(--warn)">Required: {noteRequired}</span>
              ) : null}
            </span>
            <textarea
              className={`${fieldClass} ${officeFocus} min-h-[64px] py-(--s2)`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              aria-describedby={hintId}
            />
          </label>
          {decision === 'REJECTED' ? (
            <label className="mt-(--s2) flex max-w-[320px] flex-col gap-(--s1)">
              <span className={labelClass}>
                {configs?.ncr?.enabled ? 'NCR number in D365, if one is already raised' : 'NCR number in D365 (raise it there)'}
              </span>
              <input className={`${fieldClass} ${officeFocus}`} value={ncrId} onChange={(e) => setNcrId(e.target.value)} maxLength={40} />
            </label>
          ) : null}

          <div className="mt-(--s3) flex flex-wrap items-center gap-(--s3)">
            <span title={hint ?? 'Record this decision'}>
              <Button type="button" variant="default" onClick={() => void submit()} disabled={busy || hint !== null}>
                {busy ? 'Recording…' : `Record decision on #${certificate.cofaId}`}
              </Button>
            </span>
            <p id={hintId} aria-live="polite" className="m-0 max-w-[60ch] text-(length:--fs-xs) text-(--muted)">
              {hint && hint !== noteRequired
                ? hint
                : decision === 'APPROVED'
                  ? 'Covers the batches in front of it now. Nothing is put on or off hold in D365.'
                  : decision === 'REJECTED'
                    ? rejectHint(configs)
                    : 'Puts this line on the chase list.'}
            </p>
            {latest ? (
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setChanging(false);
                  setDecision(null);
                }}
                disabled={busy}
              >
                Keep the decision
              </Button>
            ) : null}
            {!grants.known && grants.error ? (
              <Button variant="outline" type="button" onClick={grants.reload}>
                Re-read my authorisations
              </Button>
            ) : null}
          </div>
        </>
      )}

      {refusal ? (
        <p role="alert" className={`m-0 mt-(--s3) rounded-(--radius) border p-(--s3) text-(length:--fs-md) font-semibold ${TONE_CLASS.warn}`}>
          {refusal}
        </p>
      ) : null}
    </div>
  );
}

/* ── The incoming inspection at the dock (IIR, migration 017) ───────── */

/** The failed checks of a list, each with its reason, and the checks the record was never asked
 *  (null: added to the form after it, rule 15). "All passed" only covers the checks it answered. */
export function Failures({ checks }: { checks: IirCheck[] }) {
  const failed = checks.filter((c) => c.result === 'FAIL');
  const notAsked = notAskedText(checks);
  const notAskedLine = notAsked ? <span className="block text-(--muted)">{notAsked}</span> : null;
  if (failed.length === 0) {
    return (
      <>
        <span className="text-(--muted)">{notAsked ? 'All the checks asked passed' : 'All passed'}</span>
        {notAskedLine}
      </>
    );
  }
  return (
    <>
      <ul className="m-0 list-none p-0">
        {failed.map((c) => (
          <li key={c.check} className="font-semibold text-(--bad)">
            {c.label}: <span className="font-normal">{c.reason ?? 'no reason recorded'}</span>
          </li>
        ))}
      </ul>
      {notAskedLine}
    </>
  );
}

/**
 * One photo from the dock (migration 019), read through the API with the bearer token - the
 * Blob container is never public, so an <img src> to it could not work and must not. Shown
 * from an object URL; opening it shows the full picture in a new tab. A read that failed says
 * so and offers Try again (rule 15: a failed read is not "no photo").
 */
export function PhotoThumb({ inspectionId, photo, caption }: { inspectionId: number; photo: InspectionPhoto; caption: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const ctrl = new AbortController();
    let made: string | null = null;
    setFailed(null);
    api
      .blob(`/receipt-inspections/${inspectionId}/photos/${photo.photoId}`, ctrl.signal)
      .then((b) => {
        made = URL.createObjectURL(b);
        setUrl(made);
      })
      .catch((err) => {
        if (!ctrl.signal.aborted) setFailed(serverMessage(err));
      });
    return () => {
      ctrl.abort();
      if (made) URL.revokeObjectURL(made);
    };
  }, [inspectionId, photo.photoId, attempt]);
  return (
    <li className="flex w-[112px] flex-col gap-[2px]">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className={officeFocus}>
          <img src={url} alt={`Inspection photo: ${caption}`} className="h-[112px] w-[112px] rounded-(--radius) border border-(--border) object-cover" />
        </a>
      ) : (
        <span
          className="flex h-[112px] w-[112px] flex-col items-center justify-center gap-(--s1) rounded-(--radius) border border-(--border) p-(--s1) text-center text-(length:--fs-xs) text-(--muted)"
          title={failed ?? undefined}
        >
          {failed ? (
            <>
              <span>Photo could not be read</span>
              <button type="button" className={`underline ${officeFocus}`} onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </button>
            </>
          ) : (
            'Loading…'
          )}
        </span>
      )}
      <span className="text-(length:--fs-xs) text-(--muted)">{caption}</span>
    </li>
  );
}

/**
 * The dock's photos for a live record: its own and every version it corrected, each labelled
 * with the inspection it came from - a correction carries none, so this is the only way they
 * stay in sight once QA corrects a record. Unknown is said, never shown as "no photos".
 */
export function InspectionPhotos({ live, all }: { live: Inspection; all: readonly Inspection[] }) {
  const { known, photos } = lineagePhotos(live, all);
  if (known && photos.length === 0) return null;
  return (
    <div className="mt-(--s2)">
      {!known ? (
        <p className="m-0 text-(length:--fs-xs) text-(--muted)">
          Photos: not known for every version of this inspection - the server did not say. Reload to ask again.
        </p>
      ) : null}
      {photos.length > 0 ? (
        <ul className="m-0 mt-(--s1) flex list-none flex-wrap gap-(--s2) p-0" aria-label={`Photos from inspection #${live.inspectionId} and the versions it corrected`}>
          {photos.map(({ fromInspectionId, photo, checks }) => (
            <PhotoThumb
              key={`${fromInspectionId}-${photo.photoId}`}
              inspectionId={fromInspectionId}
              photo={photo}
              caption={`#${fromInspectionId} · ${photoCaption(photo, checks)}`}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The dock's inspection of the deliveries carrying this line, between the certificates and
 * the supplier. Information beside the certificate: approving one never needs it (Amit,
 * 22 Oct 2026). The CofA match is the app's, from the lot on the bag, never a receiver's tick.
 *
 * TWO STEPS, TWO CARDS (QA team feedback, 2 Nov 2026: "separate the transport inspection and the
 * samples inspection as distinct steps with distinct cards"): step 1 the vehicle (transport)
 * inspection, the warehouse's; step 2 the goods (sample) inspection, QA's (migration 033). A record
 * from before 033 (FULL, one form for both) sits in step 1, and step 2 says so.
 */
function InspectionBlock({
  inspections,
  receiptId,
  received,
  grants,
  onCorrected,
}: {
  inspections: Inspection[] | undefined;
  receiptId: number;
  /** D365 holds the goods as received: then "none recorded" is something to do, not a fact to file. */
  received: boolean;
  grants: MyGrants;
  onCorrected: () => void;
}) {
  if (inspections === undefined) {
    return (
      <>
        {([1, 2] as const).map((step) => (
          <section key={step} className={cardClass} aria-label={STEP_TITLE[step]}>
            <StepTitle step={step} />
            <p className="m-0 mt-(--s2) text-(length:--fs-sm) text-(--muted)">
              Not known: the server did not say whether this delivery was inspected at the dock.
            </p>
          </section>
        ))}
      </>
    );
  }
  // The handoff (migration 033): each vehicle inspection, then the goods inspection after it.
  const live = handoffOrder(inspections.filter((i) => i.supersededBy === null));
  const liveIds = new Set(live.map((i) => i.inspectionId));
  const transport = live.filter((i) => stageOf(i) !== 'GOODS');
  const samples = live.filter((i) => stageOf(i) === 'GOODS');
  const corrected = (goods: boolean) => inspections.filter((i) => i.supersededBy !== null && (stageOf(i) === 'GOODS') === goods);
  const record = (i: Inspection) => (
    <InspectionRecord
      key={i.inspectionId}
      i={i}
      all={inspections}
      receiptId={receiptId}
      liveIds={liveIds}
      canCorrect={grants.known && grants.holds('RELEASE')}
      onCorrected={onCorrected}
    />
  );
  // Step 2 with nothing recorded: why, read from the vehicle records in step 1.
  const awaiting = transport
    .filter((i) => stageOf(i) === 'VEHICLE')
    .flatMap((v) => {
      const after = goodsAfterVehicle(v);
      return after ? [{ v, after }] : [];
    });
  const oneForm = transport.some((i) => stageOf(i) === 'FULL');
  const canGoods = !grants.known || grants.holds('RELEASE');
  return (
    <>
      <StepCard step={1} chips={transport} empty={received ? 'Not inspected' : 'None recorded'} emptyTone={received ? 'warn' : 'quiet'}>
        {transport.length === 0 ? (
          <div className="mt-(--s2) flex flex-wrap items-center gap-(--s2)">
            <p className="m-0 text-(length:--fs-sm) text-(--muted)">
              No vehicle inspection is recorded for this line. It is not needed to decide the certificate.
            </p>
            {received ? (
              <Link to={`/op/receive?receipt=${receiptId}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                Inspect at the dock
              </Link>
            ) : null}
          </div>
        ) : null}
        {transport.map(record)}
        <CorrectedNote list={corrected(false)} />
      </StepCard>
      <StepCard
        step={2}
        chips={samples}
        empty={oneForm ? 'On the one form' : awaiting.length > 0 || received ? 'Not inspected' : 'None recorded'}
        emptyTone={awaiting.some((a) => a.after.tone !== 'quiet') || (!oneForm && received) ? 'warn' : 'quiet'}
      >
        {samples.length === 0 ? (
          <div className="mt-(--s2) flex flex-col gap-(--s2)">
            {oneForm ? (
              <p className="m-0 text-(length:--fs-sm) text-(--muted)">
                Recorded with the vehicle checks on the one-form inspection in step 1 (before 27 Oct 2026).
              </p>
            ) : null}
            {awaiting.map(({ v, after }) => (
              <div key={v.inspectionId} className="flex flex-wrap items-center gap-(--s2)">
                <p className={`m-0 text-(length:--fs-sm) font-semibold ${TONE_TEXT[after.tone]}`}>
                  After vehicle inspection #{v.inspectionId}: {after.text}
                </p>
                {after.tone === 'warn' && canGoods ? (
                  <Link to={`/op/receive?vehicle=${v.inspectionId}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                    Inspect the samples
                  </Link>
                ) : null}
              </div>
            ))}
            {/* The samples open on their own since 2 Nov 2026 (Anna, 3 Nov: "QA inspection is pending if vehicle
                inspection is not done"): never "follows a passed vehicle inspection". */}
            {!oneForm && awaiting.length === 0 ? (
              <div className="flex flex-wrap items-center gap-(--s2)">
                <p className="m-0 text-(length:--fs-sm) text-(--muted)">
                  {received ? 'No samples inspection is recorded. It does not wait for the vehicle check.' : 'Nothing received yet.'}
                </p>
                {received && canGoods ? (
                  <Link to={`/op/receive?receipt=${receiptId}&step=goods`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                    Inspect the samples
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {samples.map(record)}
        <CorrectedNote list={corrected(true)} />
      </StepCard>
    </>
  );
}

const STEP_TITLE = { 1: 'Vehicle (transport) inspection', 2: 'Goods (sample) inspection' } as const;
const STEP_WHO = { 1: 'Step 1 · the warehouse, at the truck', 2: 'Step 2 · QA, the samples (no need to wait for step 1)' } as const;

function StepTitle({ step }: { step: 1 | 2 }) {
  return (
    <span className="inline-flex flex-col">
      <span className="text-(length:--fs-xs) font-semibold uppercase tracking-wide text-(--muted)">{STEP_WHO[step]}</span>
      <span className="font-display text-[22px] font-semibold leading-tight">{STEP_TITLE[step]}</span>
    </span>
  );
}

/** One dock step as its own collapsed card. Each record's result stays on the summary line, so a
 *  FAIL is never folded out of sight. Collapsed: not needed to decide (Amit, 22 Oct 2026). */
function StepCard({
  step,
  chips,
  empty,
  emptyTone,
  children,
}: {
  step: 1 | 2;
  chips: readonly Inspection[];
  empty: string;
  emptyTone: Tone;
  children: React.ReactNode;
}) {
  return (
    <details className={cardClass} aria-label={STEP_TITLE[step]}>
      <summary className={`cursor-pointer ${officeFocus}`}>
        <span className="inline-flex flex-wrap items-center gap-(--s2) align-middle">
          <StepTitle step={step} />
          {chips.length === 0 ? (
            <span className={`${chipClass} ${TONE_CLASS[emptyTone]}`}>{empty}</span>
          ) : (
            chips.map((i) => (
              <span key={i.inspectionId} className={`${chipClass} ${TONE_CLASS[IIR_TONE[i.lineageFailed ? 'FAIL' : i.result]]}`}>
                #{i.inspectionId} {i.lineageFailed && i.result === 'PASS' ? 'Failed, corrected' : IIR_LABEL[i.result]}
              </span>
            ))
          )}
        </span>
      </summary>
      {children}
    </details>
  );
}

function CorrectedNote({ list }: { list: readonly Inspection[] }) {
  return list.length > 0 ? (
    <p className="m-0 mt-(--s2) text-(length:--fs-xs) text-(--muted)">
      Corrected and replaced: {list.map((i) => `#${i.inspectionId} by #${i.supersededBy}`).join(', ')}.
    </p>
  ) : null;
}

/** The PO line as written on the page: "PO026202" or "PO026202 / 3". */
function poLine(l: { po: string; line: number }): string {
  return `${l.po}${l.line !== 1 ? ` / ${l.line}` : ''}`;
}

/** One live record: a FULL record as before 033, a vehicle inspection with the PO lines on the truck
 *  and where its goods stand, or a goods inspection with the vehicle record it follows. */
function InspectionRecord({
  i,
  all,
  receiptId,
  liveIds,
  canCorrect,
  onCorrected,
}: {
  i: Inspection;
  all: readonly Inspection[];
  receiptId: number;
  liveIds: ReadonlySet<number>;
  canCorrect: boolean;
  onCorrected: () => void;
}) {
  const stage = stageOf(i);
  const mine = i.batches.filter((b) => b.receiptId === receiptId);
  const others = i.batches.length - mine.length;
  const failure = failureSentence(i.failure);
  const v = stage === 'GOODS' ? (i.vehicleInspection ?? null) : null;
  return (
    <div className="mt-(--s3) border-t border-(--border) pt-(--s3) first-of-type:border-t-0">
      <div className="flex flex-wrap items-center gap-(--s2)">
        {stage !== 'FULL' ? (
          <span className="text-(length:--fs-sm) font-semibold">
            {STAGE_LABEL[stage]} #{i.inspectionId}
          </span>
        ) : null}
        {/* A FAIL a correction replaced is never erased (review round 2). */}
        <span className={`${chipClass} ${TONE_CLASS[IIR_TONE[i.lineageFailed ? 'FAIL' : i.result]]}`}>
          {i.lineageFailed && i.result === 'PASS' ? 'Failed, corrected' : IIR_LABEL[i.result]}
        </span>
        <span className="text-(length:--fs-sm)">
          {stage === 'FULL'
            ? `Received ${dayLabel(i.dateReceived)} · inspected by ${i.inspectedByUpn} ${stamp(i.capturedAtDevice ?? i.recordedAt)}`
            : `${stage === 'VEHICLE' ? `Received ${dayLabel(i.dateReceived)} · ` : ''}by ${i.inspectedByUpn}, ${stamp(i.capturedAtDevice ?? i.recordedAt)}`}
          {stage !== 'GOODS' && i.deliveryCompany ? ` · ${i.deliveryCompany}` : ''}
          {stage !== 'GOODS' && i.vehicleRego ? ` · ${i.vehicleRego}` : ''}
          {i.supersedes ? ` · corrects #${i.supersedes}` : ''}
          {i.correctedByUpn ? ` · corrected by ${i.correctedByUpn}` : ''}
        </span>
      </div>
      {i.correctionNote ? <p className="m-0 mt-(--s1) text-(length:--fs-sm)">Correction: {i.correctionNote}</p> : null}
      {stage === 'GOODS' ? (
        i.vehicleInspectionId != null && liveIds.has(i.vehicleInspectionId) ? (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">Follows vehicle inspection #{i.vehicleInspectionId}, in step 1.</p>
        ) : v ? (
          <div className="mt-(--s2) rounded-(--radius) border border-(--border) p-(--s2) text-(length:--fs-sm)">
            <p className="m-0">
              <span className="font-semibold">Vehicle inspection #{v.inspectionId}</span>{' '}
              <span className={`${chipClass} ${TONE_CLASS[IIR_TONE[v.result]]}`}>{IIR_LABEL[v.result]}</span> by {v.inspectedByUpn},{' '}
              {stamp(v.recordedAt)}
              {v.deliveryCompany ? ` · ${v.deliveryCompany}` : ''}
              {v.vehicleRego ? ` · ${v.vehicleRego}` : ''}
            </p>
            <div className="mt-(--s1)">
              <Failures checks={v.checks} />
            </div>
            {v.comments ? <p className="m-0 mt-(--s1)">{v.comments}</p> : null}
          </div>
        ) : i.vehicleInspectionId == null && i.vehicleInspection === null ? (
          // On its own (2 Nov 2026): QA inspected the goods without waiting for the vehicle check.
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
            Recorded on its own, without a vehicle inspection. Received {dayLabel(i.dateReceived)}
            {i.deliveryCompany ? ` · ${i.deliveryCompany}` : ''}
            {i.vehicleRego ? ` · ${i.vehicleRego}` : ''}.
          </p>
        ) : (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
            Vehicle inspection{i.vehicleInspectionId != null ? ` #${i.vehicleInspectionId}` : ''}: not known - the server did not say.
          </p>
        )
      ) : null}
      <dl className="m-0 mt-(--s2) grid grid-cols-1 gap-(--s2) text-(length:--fs-sm) md:grid-cols-[10rem_1fr]">
        {stage !== 'GOODS' ? (
          <>
            <dt className="font-semibold">Vehicle</dt>
            <dd className="m-0">
              <Failures checks={i.vehicle} />
            </dd>
          </>
        ) : null}
        {stage === 'VEHICLE' ? (
          <>
            <dt className="font-semibold">On the truck</dt>
            <dd className="m-0">
              {i.lines === undefined || i.lines.length === 0 ? (
                <span className="text-(--muted)">PO lines not known - the server did not say.</span>
              ) : (
                <ul className="m-0 list-none p-0">
                  {i.lines.map((l) => (
                    <li key={l.receiptId}>
                      {l.receiptId === receiptId ? (
                        <span className="font-semibold">{poLine(l)}</span>
                      ) : (
                        <Link to={`/office/goods-in/${l.receiptId}`} className={`underline ${officeFocus}`}>
                          {poLine(l)}
                        </Link>
                      )}{' '}
                      {l.itemName ?? l.item}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </>
        ) : null}
        {mine.map((b) => (
          <div key={b.sequence} className="contents">
            <dt className="font-semibold">
              <span className="font-mono">{b.lotAsPrinted ?? b.batchId ?? 'no lot'}</span>
            </dt>
            <dd className="m-0">
              <Failures checks={b.checks} />
              <span className="block text-(--muted)">
                {b.manufacturedDate ? `Made ${dayLabel(b.manufacturedDate, true)} · ` : ''}
                {b.bestBeforeDate ? `Best before ${dayLabel(b.bestBeforeDate, true)} · ` : ''}
                {b.quantityKg !== null ? `${b.quantityKg} kg` : 'quantity not recorded'}
              </span>
              <span
                className={`${chipClass} mt-[2px] inline-flex ${TONE_CLASS[iirMatchText(b.cofaMatch).tone]}`}
                title={b.cofaMatch.note ?? undefined}
              >
                CofA: {iirMatchText(b.cofaMatch).text}
              </span>
              {b.cofaMatch.note ? <span className="block text-(--muted)">{b.cofaMatch.note}</span> : null}
              {b.comment ? <span className="block">{b.comment}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      {others > 0 ? (
        <p className="m-0 mt-(--s1) text-(length:--fs-xs) text-(--muted)">
          The same truck carried {others} other row{others === 1 ? '' : 's'} for other PO lines.
        </p>
      ) : null}
      {i.comments ? <p className="m-0 mt-(--s1) text-(length:--fs-sm)">{i.comments}</p> : null}
      <InspectionPhotos live={i} all={all} />
      {failure ? (
        <p className={`m-0 mt-(--s1) text-(length:--fs-sm) font-semibold ${TONE_TEXT[failure.tone]}`}>
          {failure.text}{' '}
          {i.failure?.status === 'OPEN' ? (
            <Link to="/office/inspection-failures" className={`underline ${officeFocus}`}>
              Inspection failures
            </Link>
          ) : null}
        </p>
      ) : null}
      {stage === 'VEHICLE' && i.goodsInspectionId != null && !liveIds.has(i.goodsInspectionId) ? (
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">Goods inspected: #{i.goodsInspectionId}, in step 2.</p>
      ) : null}
      {canCorrect ? <CorrectPanel inspection={i} onCorrected={onCorrected} /> : null}
    </div>
  );
}

/**
 * QA's correction of an inspection (review round 2): RELEASE, a written note, every check of the
 * whole truck shown so the correction covers every PO line the record did. REST only; there is no
 * operator-side correction in v1. A correction never erases the failure it replaces, and the
 * FAIL it replaces still shows ("Failed, corrected").
 */
function CorrectPanel({ inspection, onCorrected }: { inspection: Inspection; onCorrected: () => void }) {
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState<Record<string, { result: 'PASS' | 'FAIL' | null; reason: string }>>({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open) {
    return (
      <Button variant="outline" type="button" className="mt-(--s2)" onClick={() => setOpen(true)}>
        Correct this inspection
      </Button>
    );
  }
  // A goods inspection (migration 033) carries no vehicle answers: they are its vehicle record's, and
  // the correction keeps the stage (correction_mismatch otherwise).
  const stage = stageOf(inspection);
  // A vehicle inspection is corrected with its PO lines; unknown lines cannot be sent (rule 15).
  const linesUnknown = stage === 'VEHICLE' && !(inspection.lines && inspection.lines.length > 0);
  const rows: Array<{ key: string; where: string; c: IirCheck }> = [
    ...(stage === 'GOODS' ? [] : inspection.vehicle.map((c) => ({ key: `V:${c.check}`, where: 'Vehicle', c }))),
    ...inspection.batches.flatMap((b) =>
      b.checks.map((c) => ({ key: `${b.sequence}:${c.check}`, where: `${b.po} ${b.lotAsPrinted ?? b.batchId ?? ''}`.trim(), c })),
    ),
  ];
  const current = (key: string, c: IirCheck) => edits[key] ?? { result: c.result, reason: c.reason ?? '' };
  const missingReason = rows.some(({ key, c }) => current(key, c).result === 'FAIL' && !visibleText(current(key, c).reason));
  // A check the record was never asked (added to the form since, migration 022) stays "not asked" on
  // the correction, read-only: nobody made it at the dock, and the server refuses both an answer
  // (not_asked_answered) and a null where the record it corrects answered (check_missing).
  const notAsked = rows.some(({ c }) => c.result === null);
  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/receipt-inspections', correctionPayload(inspection, edits, note, crypto.randomUUID()));
      setOpen(false);
      onCorrected();
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-(--s2) rounded-(--radius) border border-(--border) p-(--s3)">
      <p className="m-0 text-(length:--fs-sm) font-semibold">
        Correct {stage === 'FULL' ? 'inspection' : STAGE_LABEL[stage].toLowerCase()} #{inspection.inspectionId}
      </p>
      {linesUnknown ? (
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) font-semibold text-(--warn)">
          The PO lines on this truck are not known - the server did not say - so it cannot be corrected here. Reload to ask again.
        </p>
      ) : null}
      <table className="mt-(--s2) w-full text-(length:--fs-sm)">
        <tbody>
          {rows.map(({ key, where, c }) => {
            const v = current(key, c);
            return (
              <tr key={key}>
                <td className="pr-(--s2) text-(--muted)">{where}</td>
                <td className="pr-(--s2)">{c.label}</td>
                <td className="pr-(--s2)">
                  {c.result === null ? (
                    // Read-only: the server refuses an answer here (not_asked_answered, review of #94).
                    <span className="text-(--muted)">Not asked at the dock</span>
                  ) : (
                    <select
                      aria-label={`${where} ${c.label}`}
                      className={`${fieldClass} ${officeFocus}`}
                      value={v.result ?? ''}
                      onChange={(e) => setEdits({ ...edits, [key]: { ...v, result: e.target.value as 'PASS' | 'FAIL' } })}
                    >
                      <option value="PASS">Pass</option>
                      <option value="FAIL">Fail</option>
                    </select>
                  )}
                </td>
                <td>
                  {v.result === 'FAIL' ? (
                    <input
                      aria-label={`${where} ${c.label} reason`}
                      className={`${fieldClass} ${officeFocus} w-full`}
                      value={v.reason}
                      onChange={(e) => setEdits({ ...edits, [key]: { ...v, reason: e.target.value } })}
                    />
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {notAsked ? (
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          This record predates a check on the form, so the dock never made it. It stays &quot;Not asked at the dock&quot; on the
          correction: an answer here would record a check nobody made.
        </p>
      ) : null}
      <label className="mt-(--s2) flex flex-col gap-(--s1)">
        <span className={labelClass}>Why it is corrected (required)</span>
        <textarea className={`${fieldClass} ${officeFocus}`} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error ? (
        <p role="alert" className="m-0 mt-(--s1) text-(length:--fs-sm) font-semibold text-(--warn)">
          {error}
        </p>
      ) : null}
      <div className="mt-(--s2) flex gap-(--s2)">
        <Button type="button" disabled={busy || !visibleText(note) || missingReason || linesUnknown} onClick={() => void submit()}>
          {busy ? 'Saving…' : 'Save correction'}
        </Button>
        <Button variant="outline" type="button" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default GoodsInReceipt;

/** "View specification": the version a certificate was judged against (rule 6). The spec page is keyed by
 *  SUBJECT (/office/specs/MATERIAL-7), so the version's subject is read first (GET /spec-versions/{id}).
 *  Until 29 Oct 2026 this linked /office/specs/<version id>, which names no subject.
 *  A real link that opens in a new tab, with the version in the address (UI/UX audit, 31 Oct 2026,
 *  UX-27): the button it replaces navigated away in the middle of a decision, could not be opened beside
 *  the receipt, and carried the version in router state, so a reload lost the version that was judged.
 *  `?version=` names it and `?from=` is the way back to this receipt (SpecVersion reads both). Rule 15:
 *  while the subject is being read the link is not offered; a read that fails says so, with Try again. */
export function specVersionHref(subject: string, versionId: number, from: string): string {
  return `/office/specs/${subject}?${new URLSearchParams({ version: String(versionId), from }).toString()}`;
}

function ViewSpecification({ versionId, from }: { versionId: number; from: string }) {
  const [subject, setSubject] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setFailed(null);
    api
      .get<{ version: { id: number; subjectType: SubjectType; subjectId: number } }>(`/spec-versions/${versionId}`)
      .then((r) => {
        if (live) setSubject(subjectKey(r.version.subjectType, r.version.subjectId));
      })
      .catch((err) => {
        if (live) setFailed(errorMessage(err));
      });
    return () => {
      live = false;
    };
  }, [versionId, attempt]);
  if (failed)
    return (
      <span role="alert" className="inline-flex flex-wrap items-center gap-(--s1) text-(length:--fs-xs)">
        The specification could not be found: {failed}
        <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </Button>
      </span>
    );
  if (!subject)
    return (
      <Button type="button" variant="outline" size="sm" disabled>
        View specification
      </Button>
    );
  return (
    <a
      href={specVersionHref(subject, versionId, from)}
      target="_blank"
      rel="noopener noreferrer"
      className={buttonVariants({ variant: 'outline', size: 'sm' })}
    >
      View specification<span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}
